import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Activity, Database, BarChart3, Slack, Settings, AlertTriangle, RefreshCw, ChevronDown, Loader2, Play, Wand2, ChevronRight, ListChecks, Code2 } from 'lucide-react'
import { GoogleAdsIcon } from '../components/icons/GoogleAdsIcon'
import { BetaBadge } from '../components/BetaBadge'
import SlackLayoutEditor, { type CatalogEntry, type CatalogStatus, type LayoutRow, type MetricOption } from '../features/digest-layout/SlackLayoutEditor'
import {
  MAX_ACCOUNTS_PER_AGENT,
  MAX_CAMPAIGNS_PER_AGENT,
  summarizeMode,
  useAccountAccess,
  type AccessMap,
} from '../features/google-ads/access'
import { AccessChip, ModeCard, RunOutcomeTable } from '../features/google-ads/GoogleAdsUi'
import api from '../services/api'
import TopBar from '../components/layout/TopBar'
import Sidebar from '../components/layout/Sidebar'
import { useAuthStore } from '../stores/authStore'
import SkillSelector from '../features/agent-skills/SkillSelector'
import { getAvailableSkills, getDefaultSkills, inferSkillsFromLegacy } from '../features/agent-skills/SkillCatalog'
import './AddAgentWizard.css'

const DEFAULT_GOOGLE_ADS_CONFIG = {
  targets: '',
  ml_model: 'logistic_regression',
  check_frequency: 'daily',
  check_time: '01:00',
  auto_pause_campaigns: true,
  auto_adjust_budgets: true,
  auto_add_keywords: true,
  auto_add_negatives: true,
  auto_approve_budget_change_percent: 15,  // Lowered from 20 to 15 for safety
  batch_size: 1000,
  ml_training_days: 90,
  read_only_mode: true,  // Safe mode: validate and report, never change the account
  auto_remove_keywords: true,
  max_daily_budget_usd: 10000,  // $10k/day absolute cap
  max_budget_increase_pct: 50,  // 50% max increase per action
  max_total_budget_increase_pct: 10,  // account-wide net increase per run
  slack_channel_id: '',
  slack_channel: '',
}

interface ColumnInfo {
  name: string
  type: string
  mode?: string
  description?: string
  is_partition?: boolean
  is_clustering?: boolean
}

interface TableSchema {
  columns: ColumnInfo[]
  row_count?: number
  size_label?: string
  last_modified?: string | null
  description?: string
  partition_field?: string | null
}

interface DatasetGroup {
  dataset: string
  tables: string[]
}

interface SlackTable {
  heading: string
  emoji?: string
  headers: string[]
  rows: string[][]
}

interface SlackPreview {
  title: string
  analysis_date: string
  performance: SlackTable
  sections: SlackTable[]
}

function PreviewTable({ table }: { table: SlackTable }) {
  return (
    <div className="st-premade-table" style={{ marginBottom: 14 }}>
      <table className="st-result-table">
        <thead>
          <tr>
            {table.headers.map((h, i) => (
              <th key={i}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((row, r) => (
            <tr key={r}>
              {row.map((cell, c) => (
                <td key={c}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

interface SqlTemplate {
  id: string
  name: string
  description: string
  requires: string[] // table name prefixes, e.g. tbl_sort_
  // Slack detail tables; default = shown, metrics = default columns, dim = first column
  layout: Array<{ id: string; heading: string; default: boolean; metrics: string[]; dim: string }>
}

// A Slack detail table id, or {id, metrics} when the agent picked its own columns.
type LayoutItem = string | { id: string; metrics: string[] }
const layoutId = (x: LayoutItem) => (typeof x === 'string' ? x : x.id)

interface RowFilter {
  column: string
  operator: string
  value: string
}

interface SlackChannel {
  id: string
  name: string
  label?: string
}

interface PremadeQuery {
  name: string
  description: string
  sql: string
  questions: string[]
  verified: boolean
  _expanded?: boolean
  _testing?: boolean
  _testResult?: {
    ok?: boolean
    row_count?: number
    bytes_processed?: number
    columns?: string[]
    rows?: Record<string, string>[]
    formatted_table?: string
    error?: string
  } | null
}

type DemandPulseDataset = {
  id: string
  label: string
  tables: string[]
  has_forecast: boolean
  recommended: boolean
}

interface WizardData {
  task_name: string
  display_name: string
  description: string
  bq_project: string
  bq_dataset: string
  tables: Array<{ dataset: string; table: string }>  // Changed to store dataset + table
  context: string
  questions: string[]
  allowed_users: string[]
  premade_queries: PremadeQuery[]
  digest_enabled: boolean
  schedule_hour: number
  custom_cron: boolean
  schedule: string
  slack_channel: string
  slack_channel_id: string
  verticals: string[]
  column_filters: Record<string, string[]>
  column_value_filters: Record<string, RowFilter[]>
  skills: string[]
  digest_snapshot_sql: string
  digest_template: string
  digest_layout: LayoutItem[]
  data_lag_days: number
  max_days_behind: number
  google_ads_customer_ids: string[]
  google_ads_campaign_ids: string[]
  demand_pulse_datasets: string[]
  google_ads_config: {
    targets: string
    ml_model: string
    check_frequency: string
    check_time: string
    auto_pause_campaigns: boolean
    auto_adjust_budgets: boolean
    auto_add_keywords: boolean
    auto_add_negatives: boolean
    auto_approve_budget_change_percent: number
    batch_size: number
    ml_training_days: number
    read_only_mode: boolean
    auto_remove_keywords: boolean
    max_daily_budget_usd: number
    max_budget_increase_pct: number
    max_total_budget_increase_pct: number
    slack_channel_id: string
    slack_channel: string
  } | null
}

function colTypeCls(type: string): string {
  const t = (type || '').toUpperCase()
  if (['DATE', 'DATETIME', 'TIMESTAMP'].includes(t)) return 'type-date'
  if (['INTEGER', 'INT64', 'FLOAT', 'FLOAT64', 'NUMERIC', 'BIGNUMERIC'].includes(t)) return 'type-num'
  if (['BOOL', 'BOOLEAN'].includes(t)) return 'type-bool'
  if (['RECORD', 'STRUCT', 'ARRAY'].includes(t)) return 'type-struct'
  return 'type-str'
}

function modifiedLabel(iso?: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  const ago = Math.round((Date.now() - d.getTime()) / 86400000)
  if (ago === 0) return 'Modified today'
  if (ago === 1) return 'Modified yesterday'
  return `Modified ${ago} days ago`
}

function ChipInput({
  values,
  onChange,
  placeholder,
  suggestions,
}: {
  values: string[]
  onChange: (next: string[]) => void
  placeholder: string
  suggestions?: Array<{ value: string; label: string; badge?: string; badgeCls?: string }>
}) {
  const [draft, setDraft] = useState('')
  const [open, setOpen] = useState(false)
  const q = draft.trim().toLowerCase()
  const filtered = (suggestions || [])
    .filter((s) => !values.includes(s.value) && (!q || s.value.toLowerCase().includes(q) || s.label.toLowerCase().includes(q)))
    .slice(0, 30)

  const add = (raw: string) => {
    const v = raw.trim().toLowerCase().replace(/,$/, '')
    if (!v) return
    if (!values.includes(v)) onChange([...values, v])
    setDraft('')
    setOpen(false)
  }

  return (
    <div style={{ position: 'relative' }}>
      <div className="st-chips-input" onClick={() => setOpen(true)}>
        {values.map((v) => (
          <span key={v} className="chip">
            {v}
            <button type="button" title="Remove" onClick={() => onChange(values.filter((x) => x !== v))}>
              ×
            </button>
          </span>
        ))}
        <input
          type="text"
          value={draft}
          placeholder={placeholder}
          onChange={(e) => {
            setDraft(e.target.value)
            setOpen(true)
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault()
              add(draft)
            } else if (e.key === 'Backspace' && !draft && values.length) {
              onChange(values.slice(0, -1))
            }
          }}
        />
      </div>
      {open && filtered.length > 0 && (
        <div className="st-autocomplete">
          {filtered.map((s) => (
            <button
              key={s.value}
              type="button"
              className="st-ac-item"
              onMouseDown={(e) => {
                e.preventDefault()
                add(s.value)
              }}
            >
              <span>{s.label}</span>
              {s.badge && <span className={s.badgeCls || 'ok'}>{s.badge}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function ResultTable({ columns, rows }: { columns: string[]; rows: Record<string, string>[] }) {
  if (!columns.length) return <div className="st-hint">No rows returned.</div>
  return (
    <div className="st-premade-table">
      <table className="st-result-table">
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              {columns.map((c) => (
                <td key={c}>{r[c] ?? ''}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function ColumnPanel({
  table,
  schema,
  loading,
  error,
  columnFilters,
  valueFilters,
  onToggleColumn,
  onSelectAll,
  onSelectNone,
  onAddFilter,
  onRemoveFilter,
}: {
  table: string
  schema?: TableSchema
  loading: boolean
  error?: string
  columnFilters: Record<string, string[]>
  valueFilters: Record<string, RowFilter[]>
  onToggleColumn: (table: string, col: string, on: boolean) => void
  onSelectAll: (table: string) => void
  onSelectNone: (table: string) => void
  onAddFilter: (table: string, filter: RowFilter) => void
  onRemoveFilter: (table: string, index: number) => void
}) {
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  const [typeOpen, setTypeOpen] = useState(false)
  const [showVfForm, setShowVfForm] = useState(false)
  const [vfDraft, setVfDraft] = useState<RowFilter>({ column: '', operator: '=', value: '' })

  if (loading) return <div className="st-col-loading">Loading schema…</div>
  if (error) return <div className="st-col-error">{error}</div>
  if (!schema) return null

  const cols = schema.columns || []
  const filter = columnFilters[table]
  const selectedSet = filter ? new Set(filter) : null
  const selectedCount = filter ? filter.length : cols.length
  const activeVF = valueFilters[table] || []

  const visible = cols.filter((c) => {
    const matchSearch = !search || c.name.toLowerCase().includes(search.toLowerCase())
    const cls = colTypeCls(c.type).replace('type-', '')
    const matchType = !typeFilter || cls === typeFilter
    return matchSearch && matchType
  })

  return (
    <>
      <div className="st-col-meta">
        {schema.row_count != null && <span>{Number(schema.row_count).toLocaleString()} rows</span>}
        {schema.size_label && <span>{schema.size_label}</span>}
        {schema.last_modified && <span>{modifiedLabel(schema.last_modified)}</span>}
      </div>
      {schema.description && <div className="st-col-table-desc">{schema.description}</div>}
      {schema.partition_field && (
        <div className="st-col-partition-note">
          ⚡ Partitioned on <code>{schema.partition_field}</code> — agent should always filter this column to control query cost
        </div>
      )}

      {activeVF.length ? (
        <div className="st-vf-bar">
          <span className="st-vf-label">Mandatory filters:</span>
          {activeVF.map((f, fi) => (
            <span key={fi} className="st-vf-chip">
              {f.column} {f.operator} <b>{f.value}</b>
              <button type="button" className="st-vf-remove" onClick={() => onRemoveFilter(table, fi)} title="Remove filter">
                ×
              </button>
            </span>
          ))}
        </div>
      ) : (
        <div className="st-vf-bar empty">
          <span className="st-vf-label" style={{ color: 'var(--text-muted)' }}>
            No mandatory filters — agent sees all rows
          </span>
        </div>
      )}

      <div className="st-col-toolbar">
        <input
          type="search"
          className="st-col-search"
          placeholder="Filter columns…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="st-col-type-filter-wrap">
          <button
            type="button"
            className={`st-type-filter-btn${typeFilter ? ' active' : ''}`}
            onClick={() => setTypeOpen((o) => !o)}
          >
            {typeFilter ? ({ date: 'Date / Time', num: 'Number', str: 'Text', bool: 'Boolean', struct: 'Record' } as any)[typeFilter] : 'Type'} ▾
          </button>
          {typeOpen && (
            <div className="st-type-dropdown">
              {[
                { type: '', label: 'All' },
                { type: 'date', label: 'Date / Time' },
                { type: 'num', label: 'Number' },
                { type: 'str', label: 'Text' },
                { type: 'bool', label: 'Boolean' },
                { type: 'struct', label: 'Record' },
              ].map((item) => (
                <button
                  key={item.type || 'all'}
                  type="button"
                  className={`st-type-item${typeFilter === item.type ? ' active' : ''}`}
                  onClick={() => {
                    setTypeFilter(item.type)
                    setTypeOpen(false)
                  }}
                >
                  {item.label}
                </button>
              ))}
            </div>
          )}
        </div>
        <span className="st-col-count">
          {selectedCount}/{cols.length} selected
        </span>
        <button type="button" className="st-col-action" onClick={() => onSelectAll(table)}>
          All
        </button>
        <button type="button" className="st-col-action" onClick={() => onSelectNone(table)}>
          None
        </button>
        <button
          type="button"
          className="st-col-action st-vf-open-btn"
          style={{ color: 'var(--text-muted)', borderLeft: '1px solid var(--border)', paddingLeft: 8, marginLeft: 2 }}
          onClick={() => setShowVfForm((s) => !s)}
        >
          + row filter
        </button>
      </div>

      {showVfForm && (
        <div className="st-vf-add-form">
          <select
            className="st-vf-col-sel"
            value={vfDraft.column}
            onChange={(e) => setVfDraft({ ...vfDraft, column: e.target.value })}
          >
            <option value="">— column —</option>
            {cols.map((c) => (
              <option key={c.name} value={c.name}>
                {c.name}
              </option>
            ))}
          </select>
          <select
            className="st-vf-op"
            value={vfDraft.operator}
            onChange={(e) => setVfDraft({ ...vfDraft, operator: e.target.value })}
          >
            {['=', '!=', '>=', '>', '<=', '<', 'IN', 'NOT IN', 'LIKE', 'IS NULL', 'IS NOT NULL'].map((op) => (
              <option key={op} value={op}>
                {op}
              </option>
            ))}
          </select>
          <input
            type="text"
            className="st-vf-val"
            placeholder="e.g. Health, 2026-01-01"
            value={vfDraft.value}
            onChange={(e) => setVfDraft({ ...vfDraft, value: e.target.value })}
          />
          <button
            type="button"
            className="st-btn small"
            onClick={() => {
              if (!vfDraft.column) return
              if (!vfDraft.value && !['IS NULL', 'IS NOT NULL'].includes(vfDraft.operator)) return
              onAddFilter(table, { ...vfDraft })
              setVfDraft({ column: '', operator: '=', value: '' })
              setShowVfForm(false)
            }}
          >
            Add
          </button>
          <button type="button" className="st-col-action" onClick={() => setShowVfForm(false)}>
            Cancel
          </button>
        </div>
      )}

      <div className="st-col-list">
        {visible.map((c) => {
          const checked = selectedSet ? selectedSet.has(c.name) : true
          const hasVF = activeVF.some((f) => f.column === c.name)
          return (
            <div key={c.name} className={`st-col-row-outer${hasVF ? ' has-vf' : ''}`}>
              <label className={`st-col-row${c.is_partition ? ' is-partition' : ''}`}>
                <input
                  type="checkbox"
                  className="st-col-cb"
                  checked={checked}
                  onChange={(e) => onToggleColumn(table, c.name, e.target.checked)}
                />
                <span className="st-col-name">{c.name}</span>
                <span className={`st-col-type ${colTypeCls(c.type)}`}>{c.type}</span>
                {c.is_partition && <span className="st-col-flag partition">⚡ PARTITION</span>}
                {c.is_clustering && <span className="st-col-flag cluster">≡ CLUSTER</span>}
                {c.mode === 'REQUIRED' && <span className="st-col-flag required">REQUIRED</span>}
                {c.description && (
                  <span className="st-col-desc" title={c.description}>
                    {c.description}
                  </span>
                )}
              </label>
            </div>
          )
        })}
        {visible.length === 0 && <div className="st-col-loading">No columns match filter</div>}
      </div>
    </>
  )
}

// ── Google Ads campaign picker ────────────────────────────────────────────────

const GADS_MODEL_LABEL: Record<string, string> = {
  none: 'Statistics only',
  logistic_regression: 'Poisson GLM',
  gradient_boosting: 'Gradient boosting',
  random_forest: 'Random forest',
}

interface GAdsCampaign {
  customer_id: string
  account_name: string
  campaign_id: string
  campaign_name: string
  status: string
  channel_type: string
}

function savedCampaignSelection(
  campaigns: GAdsCampaign[],
  saved: { google_ads_campaign_ids?: string[]; google_ads_customer_ids?: string[] },
): Set<string> {
  const campaignIds = new Set(saved.google_ads_campaign_ids || [])
  if (campaignIds.size) {
    return new Set(campaigns.filter((c) => campaignIds.has(c.campaign_id)).map((c) => c.campaign_id))
  }
  // Agents saved before campaign IDs were stored: select every campaign in their accounts.
  const customerIds = new Set(saved.google_ads_customer_ids || [])
  return new Set(campaigns.filter((c) => customerIds.has(c.customer_id)).map((c) => c.campaign_id))
}

function GoogleAdsCampaignPicker({
  campaigns,
  loading,
  refreshing,
  fetchError,
  selectedCustomerIds,
  selectedCampaignIds,
  setSelectedCampaignIds,
  onSelectionChange,
  access,
  checkingAccess,
  onRecheckAccess,
  onRefresh,
}: {
  campaigns: GAdsCampaign[]
  loading: boolean
  refreshing: boolean
  fetchError: string
  selectedCustomerIds: string[]
  selectedCampaignIds: Set<string>
  setSelectedCampaignIds: React.Dispatch<React.SetStateAction<Set<string>>>
  onSelectionChange: (customerIds: string[], campaignIds: string[]) => void
  access: AccessMap
  checkingAccess: Set<string>
  onRecheckAccess: (customerId: string) => void
  onRefresh: () => void
}) {
  const [limitNote, setLimitNote] = useState('')
  const accountsOf = (ids: Set<string>) =>
    new Set(campaigns.filter((c) => ids.has(c.campaign_id)).map((c) => c.customer_id))
  const selectedAccounts = accountsOf(selectedCampaignIds)
  const atCampaignLimit = selectedCampaignIds.size >= MAX_CAMPAIGNS_PER_AGENT
  const atAccountLimit = selectedAccounts.size >= MAX_ACCOUNTS_PER_AGENT
  const limitTip =
    `Limit reached: one agent can manage up to ${MAX_ACCOUNTS_PER_AGENT} accounts and ` +
    `${MAX_CAMPAIGNS_PER_AGENT} campaigns. Create another agent for more.`
  const blocked = (c: GAdsCampaign) =>
    !selectedCampaignIds.has(c.campaign_id) &&
    (atCampaignLimit || (atAccountLimit && !selectedAccounts.has(c.customer_id)))

  const commit = (next: Set<string>) => {
    const accounts = accountsOf(next)
    if (next.size > MAX_CAMPAIGNS_PER_AGENT || accounts.size > MAX_ACCOUNTS_PER_AGENT) {
      setLimitNote(limitTip)
      return
    }
    setLimitNote('')
    setSelectedCampaignIds(next)
    const picked = campaigns.filter((c) => next.has(c.campaign_id))
    onSelectionChange(
      [...new Set(picked.map((c) => c.customer_id))],
      picked.map((c) => c.campaign_id),
    )
  }

  const handleToggleCampaign = (campaign: GAdsCampaign) => {
    if (blocked(campaign)) return
    const next = new Set(selectedCampaignIds)
    if (next.has(campaign.campaign_id)) next.delete(campaign.campaign_id)
    else next.add(campaign.campaign_id)
    commit(next)
  }

  const handleToggleAccount = (accountCampaigns: GAdsCampaign[]) => {
    const allSelected = accountCampaigns.every((c) => selectedCampaignIds.has(c.campaign_id))
    const next = new Set(selectedCampaignIds)
    accountCampaigns.forEach((c) => (allSelected ? next.delete(c.campaign_id) : next.add(c.campaign_id)))
    commit(next)
  }

  // Group by account
  const byAccount: Record<string, GAdsCampaign[]> = {}
  for (const c of campaigns) {
    if (!byAccount[c.customer_id]) byAccount[c.customer_id] = []
    byAccount[c.customer_id].push(c)
  }

  return (
    <div className="st-field">
      <div className="gads-picker-head">
        <label>
          Google Ads campaigns to manage <span className="req">*</span>
        </label>
        <button
          type="button"
          className="gads-refresh"
          onClick={onRefresh}
          disabled={loading || refreshing}
          aria-busy={refreshing || checkingAccess.size > 0}
          title="Reload accounts and campaigns, and recheck the agent's access in each account"
        >
          <RefreshCw
            size={13}
            aria-hidden
            className={refreshing || checkingAccess.size ? 'spinning' : undefined}
          />
          Refresh
        </button>
      </div>
      {loading && (
        <div className="st-col-loading">Loading campaigns…</div>
      )}
      {fetchError && (
        <div className="st-hint" style={{ color: 'var(--danger, #e74c3c)' }}>
          {fetchError}
        </div>
      )}
      {!loading && !fetchError && campaigns.length === 0 && (
        <div className="st-hint">No campaigns found under the manager account.</div>
      )}
      {!loading && (fetchError || campaigns.length === 0) && (
        <GoogleAdsAccessHint prominent />
      )}
      {!fetchError && campaigns.length > 0 && (
        <div
          style={{
            border: '1px solid var(--border)',
            borderRadius: 6,
            maxHeight: 320,
            overflowY: 'auto',
          }}
        >
          {Object.entries(byAccount).map(([customerId, acctCampaigns]) => {
            const allSel = acctCampaigns.every((c) => selectedCampaignIds.has(c.campaign_id))
            const someSel = acctCampaigns.some((c) => selectedCampaignIds.has(c.campaign_id))
            return (
              <div key={customerId} style={{ borderBottom: '1px solid var(--border)' }}>
                <div
                  className="gads-account-row"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 12px',
                    background: 'var(--surface-2, var(--surface))',
                    cursor: 'pointer',
                    fontWeight: 600,
                    fontSize: 13,
                  }}
                  onClick={() => handleToggleAccount(acctCampaigns)}
                >
                  <input
                    type="checkbox"
                    checked={allSel}
                    ref={(el) => { if (el) el.indeterminate = !allSel && someSel }}
                    onChange={() => handleToggleAccount(acctCampaigns)}
                    onClick={(e) => e.stopPropagation()}
                    style={{ marginRight: 4 }}
                  />
                  {acctCampaigns[0]?.account_name || customerId}
                  <span style={{ color: 'var(--text-muted)', fontWeight: 400, marginLeft: 4 }}>
                    ({customerId})
                  </span>
                  <AccessChip
                    access={access[customerId]}
                    checking={checkingAccess.has(customerId)}
                    onRecheck={() => onRecheckAccess(customerId)}
                  />
                </div>
                {acctCampaigns.map((c) => (
                  <div
                    key={c.campaign_id}
                    className={blocked(c) ? 'gads-row-disabled' : undefined}
                    title={blocked(c) ? limitTip : undefined}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '6px 12px 6px 32px',
                      cursor: 'pointer',
                      fontSize: 13,
                    }}
                    onClick={() => handleToggleCampaign(c)}
                  >
                    <input
                      type="checkbox"
                      checked={selectedCampaignIds.has(c.campaign_id)}
                      disabled={blocked(c)}
                      onChange={() => handleToggleCampaign(c)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <span style={{ flex: 1 }}>{c.campaign_name}</span>
                    <span
                      style={{
                        fontSize: 11,
                        padding: '1px 6px',
                        borderRadius: 10,
                        background: c.status === 'ENABLED' ? 'var(--success-bg, #1a4a2e)' : 'var(--surface-3, #333)',
                        color: c.status === 'ENABLED' ? 'var(--success, #4caf50)' : 'var(--text-muted)',
                      }}
                    >
                      {c.status}
                    </span>
                  </div>
                ))}
              </div>
            )
          })}
        </div>
      )}
      {!fetchError && campaigns.length > 0 && (
        <div className="gads-counter" aria-live="polite">
          <span className={atAccountLimit || atCampaignLimit ? 'at-limit' : undefined}>
            {selectedCustomerIds.length} account{selectedCustomerIds.length !== 1 ? 's' : ''} ·{' '}
            {selectedCampaignIds.size} campaign{selectedCampaignIds.size !== 1 ? 's' : ''} selected
          </span>
          <span>
            (max {MAX_ACCOUNTS_PER_AGENT} accounts, {MAX_CAMPAIGNS_PER_AGENT} campaigns)
          </span>
        </div>
      )}
      {limitNote && (
        <div className="st-hint" role="alert" style={{ marginTop: 4, color: 'var(--yellow)' }}>
          {limitNote}
        </div>
      )}
      {!fetchError && campaigns.length > 0 && <GoogleAdsAccessHint />}
    </div>
  )
}

type GAdsAccess = { service_account: string }
let gadsAccessRequest: Promise<GAdsAccess | null> | null = null

function GoogleAdsAccessHint({ prominent = false }: { prominent?: boolean }) {
  const [access, setAccess] = useState<GAdsAccess | null>(null)

  useEffect(() => {
    gadsAccessRequest ??= api
      .get('/api/google-ads/access')
      .then((res) => res.data as GAdsAccess)
      .catch(() => null)
    let alive = true
    gadsAccessRequest.then((a) => alive && setAccess(a))
    return () => {
      alive = false
    }
  }, [])

  const sa = access?.service_account || 'vertex-data-analyst-sa@fm-agent-builder.iam.gserviceaccount.com'
  const who = <code style={{ userSelect: 'all', wordBreak: 'break-all' }}>{sa}</code>
  const where = 'in Google Ads (Admin → Access and security)'

  if (!prominent) {
    return (
      <div className="st-hint" style={{ marginTop: 6, fontSize: 12, opacity: 0.8 }}>
        Missing an account? Add {who} as a user {where}. Read only is enough for reports; give it Standard
        access to let the agent make changes.
      </div>
    )
  }
  return (
    <div
      className="st-hint"
      style={{
        marginTop: 8,
        padding: '10px 12px',
        border: '1px solid var(--warning, #d4a017)',
        borderRadius: 6,
        background: 'var(--warning-bg, rgba(212, 160, 23, 0.08))',
      }}
    >
      <strong>Accounts only appear here once the agent has access.</strong> Add {who} as a user {where},
      then reload this page. Read only access is enough for reports; Standard access lets the agent make changes.
    </div>
  )
}

export default function AddAgentPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuthStore()
  
  // Get task_name from query param if direct URL navigation
  const searchParams = new URLSearchParams(location.search)
  const taskFromUrl = searchParams.get('task')
  
  const [editAgent, setEditAgent] = useState<any>((location.state as any)?.editAgent || null)
  const isEditMode = !!editAgent || !!taskFromUrl

  // Check what was passed in for edit mode
  // (Debug logs removed)

  const [theme, setTheme] = useState<'dark' | 'light'>('dark')
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [canManageAgents, setCanManageAgents] = useState(false)
  const [isAdmin, setIsAdmin] = useState(false)
  const [loading, setLoading] = useState(true)
  const [step, setStep] = useState(1)
  const [busy, setBusy] = useState('')
  const [error, setError] = useState('')
  const [tableSearch, setTableSearch] = useState('')
  const [showCtxEditor, setShowCtxEditor] = useState(false)

  // Google Ads campaigns — fetched once, not inside the picker to avoid remount loops
  const [gadsCampaigns, setGadsCampaigns] = useState<GAdsCampaign[]>([])
  const [gadsCampaignsLoading, setGadsCampaignsLoading] = useState(false)
  const [gadsCampaignsError, setGadsCampaignsError] = useState('')
  const [gadsFetched, setGadsFetched] = useState(false)
  const [gadsLoadedOnce, setGadsLoadedOnce] = useState(false)
  const [gadsSelectedCampaignIds, setGadsSelectedCampaignIds] = useState<Set<string>>(new Set())
  const [gadsRunBusy, setGadsRunBusy] = useState(false)
  const [gadsRunReport, setGadsRunReport] = useState('')
  const [gadsModelOpen, setGadsModelOpen] = useState(false)
  const [gadsRunResult, setGadsRunResult] = useState<{ accounts: any[]; decisions: any[] } | null>(null)
  const [gadsRunError, setGadsRunError] = useState('')
  const gadsAccountIds = useMemo(() => [...new Set(gadsCampaigns.map((c) => c.customer_id))], [gadsCampaigns])
  const gadsAccess = useAccountAccess(gadsAccountIds)

  const runGoogleAdsNow = async (postToSlack: boolean) => {
    setGadsRunBusy(true)
    setGadsRunError('')
    setGadsRunReport('')
    setGadsRunResult(null)
    try {
      const res = await api.post(`/api/google-ads/run/${encodeURIComponent(data.task_name)}`, {
        post_to_slack: postToSlack,
        google_ads_config: { ...DEFAULT_GOOGLE_ADS_CONFIG, ...(data.google_ads_config || {}) },
        google_ads_customer_ids: data.google_ads_customer_ids || [],
        google_ads_campaign_ids: data.google_ads_campaign_ids || [],
      })
      const slack = res.data.slack
      const posted = slack?.status === 'posted' ? `\n\nPosted to Slack channel ${slack.channel}.` : ''
      const skipped = slack?.status === 'skipped' ? `\n\nNot posted to Slack: ${slack.reason}.` : ''
      setGadsRunResult(res.data.result || null)
      setGadsRunReport((res.data.report || 'No report returned.') + posted + skipped)
    } catch (err: any) {
      setGadsRunError(err?.response?.data?.detail || err?.message || 'Run failed')
    } finally {
      setGadsRunBusy(false)
    }
  }

  // Helper to extract clean error messages from API responses
  const extractErrorMessage = (err: any, fallback: string): string => {
    // Check for detail string (HTTPException)
    if (err?.response?.data?.detail && typeof err.response.data.detail === 'string') {
      return err.response.data.detail
    }
    // Check for 422 validation errors (array of objects)
    if (Array.isArray(err?.response?.data)) {
      const firstError = err.response.data[0]
      if (firstError?.msg) {
        return firstError.msg
      }
    }
    // Fallback to generic error message
    return err?.message || fallback
  }

  const [projects, setProjects] = useState<string[]>([])
  const [catalog, setCatalog] = useState<DatasetGroup[]>([])
  const [loadingCatalog, setLoadingCatalog] = useState(false)
  const catalogProject = useRef('')
  const [channels, setChannels] = useState<SlackChannel[]>([])
  const [permGroups, setPermGroups] = useState<Record<string, unknown>>({})
  const [appUsers, setAppUsers] = useState<Array<{ email: string; first_name?: string; last_name?: string }>>([])
  const [schemaCache, setSchemaCache] = useState<Record<string, TableSchema>>({})
  const [schemaLoading, setSchemaLoading] = useState<Record<string, boolean>>({})
  const [schemaError, setSchemaError] = useState<Record<string, string>>({})
  const [expandedTables, setExpandedTables] = useState<Record<string, boolean>>({})
  const [collapsedDatasets, setCollapsedDatasets] = useState<Record<string, boolean>>({})
  const [showToast, setShowToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)

  // Auto-hide toast
  useEffect(() => {
    if (showToast && showToast.type === 'error') {
      const timer = setTimeout(() => setShowToast(null), 5000)
      return () => clearTimeout(timer)
    }
  }, [showToast])

  const initialSchedule = editAgent?.schedule || '0 9 * * *'
  const hourMatch = /^0\s+(\d{1,2})\s+\*\s+\*\s+\*$/.exec(initialSchedule)

  const [bqSkillExpanded, setBqSkillExpanded] = useState(true)
  const [slackSkillExpanded, setSlackSkillExpanded] = useState(true)
  const [chartsSkillExpanded, setChartsSkillExpanded] = useState(true)
  const [googleAdsSkillExpanded, setGoogleAdsSkillExpanded] = useState(true)
  const [demandPulseSkillExpanded, setDemandPulseSkillExpanded] = useState(true)
  const [digestSqlTesting, setDigestSqlTesting] = useState(false)
  const [digestSqlTestResult, setDigestSqlTestResult] = useState<any>(null)
  const [sqlTestPopup, setSqlTestPopup] = useState<any>(null) // Test query result, shown in a dialog
  const [premadeOpen, setPremadeOpen] = useState(false) // premade queries panel; closed by default
  const [sqlOpen, setSqlOpen] = useState(false) // digest snapshot SQL editor; hidden by default
  const [sqlTemplates, setSqlTemplates] = useState<SqlTemplate[]>([])
  const [selectedTemplate, setSelectedTemplate] = useState('')
  const [generatingTemplate, setGeneratingTemplate] = useState('')
  const [slackPreview, setSlackPreview] = useState<SlackPreview | null>(null)
  const [previewing, setPreviewing] = useState(false)
  const [metricOptions, setMetricOptions] = useState<MetricOption[]>([])
  const [metricCatalog, setMetricCatalog] = useState<CatalogEntry[] | null>(null)
  const [catalogStatus, setCatalogStatus] = useState<CatalogStatus>({ state: 'idle' })
  const [catalogSql, setCatalogSql] = useState('') // the SQL text the catalog was read from
  const [perfDef, setPerfDef] = useState<{ id: string; heading: string; dim: string; metrics: string[] } | null>(null)
  const [data, setData] = useState<WizardData>(() => {
    // Convert old format (string[]) to new format ({dataset, table}[])
    let tables: Array<{ dataset: string; table: string }> = []
    if (editAgent?.bq_tables) {
      if (editAgent.bq_tables.length > 0 && typeof editAgent.bq_tables[0] === 'string') {
        const dataset = editAgent.bq_dataset || ''
        tables = editAgent.bq_tables.map((t: string) => {
          // Check if table name contains a dot (new multi-dataset format)
          if (t.includes('.')) {
            const parts = t.split('.')
            if (parts.length === 2) {
              // Format: "dataset.table"
              return { dataset: parts[0], table: parts[1] }
            } else if (parts.length === 3) {
              // Format: "project.dataset.table" (shouldn't happen but handle it)
              return { dataset: parts[1], table: parts[2] }
            }
          }
          // Old format: just table name, use agent's dataset
          return { dataset, table: t }
        })
      } else {
        // New format: [{dataset, table}, ...]
        tables = [...editAgent.bq_tables]
      }
    }

    // Infer skills from legacy config or use provided skills
    const skills = editAgent?.skills?.length 
      ? [...editAgent.skills]
      : editAgent
        ? inferSkillsFromLegacy(editAgent)
        : getDefaultSkills()

    const initialData = {
      task_name: editAgent?.task_name || '',
      display_name: editAgent?.display_name || '',
      description: editAgent?.description || '',
      bq_project: editAgent?.bq_project || '',
      bq_dataset: editAgent?.bq_dataset || '',
      tables,
      context: editAgent?.context || '',
      questions: editAgent?.questions?.length ? [...editAgent.questions] : [''],
      allowed_users: editAgent?.allowed_users ? [...editAgent.allowed_users] : [],
      premade_queries: (editAgent?.premade_queries || []).map((pq: any) => ({
        name: pq.name || '',
        description: pq.description || '',
        sql: pq.sql || '',
        questions: (pq.questions || []).slice(),
        verified: !!pq.verified,
        _expanded: false,
        _testing: false,
        _testResult: null,
      })),
      digest_enabled: editAgent ? editAgent.digest_enabled !== false && !!(editAgent.schedule && String(editAgent.schedule).trim()) : false,
      schedule_hour: hourMatch ? +hourMatch[1] : 9,
      custom_cron: !hourMatch && !!editAgent?.schedule,
      schedule: initialSchedule,
      slack_channel: editAgent?.slack_channel || '',
      slack_channel_id: editAgent?.slack_channel_id || '',
      verticals: editAgent?.slack_vertical ? [...editAgent.slack_vertical] : [],
      column_filters: { ...(editAgent?.column_filters || {}) },
      column_value_filters: JSON.parse(JSON.stringify(editAgent?.column_value_filters || {})),
      skills,
      digest_snapshot_sql: editAgent?.digest_snapshot_sql || '',
      digest_template: editAgent?.digest_template || '',
      digest_layout: editAgent?.digest_layout ? [...editAgent.digest_layout] : [],
      data_lag_days: editAgent?.data_lag_days ?? 3,
      max_days_behind: editAgent?.max_days_behind ?? 4,
      google_ads_customer_ids: editAgent?.google_ads_customer_ids ? [...editAgent.google_ads_customer_ids] : [],
      google_ads_campaign_ids: editAgent?.google_ads_campaign_ids ? [...editAgent.google_ads_campaign_ids] : [],
      google_ads_config: editAgent?.google_ads_config ? { ...editAgent.google_ads_config } : null,
      demand_pulse_datasets: editAgent?.demand_pulse_datasets ? [...editAgent.demand_pulse_datasets] : [],
    };
    
    return initialData;
  })

  // Duplicates keep the source agent's tables so copied SQL/context can be
  // renamed to the newly selected tables (paired by prefix, e.g. tbl_sort_).
  const [dupSourceTables, setDupSourceTables] = useState(() =>
    editAgent && !editAgent.task_name ? data.tables : null
  )
  const tableRenames = useMemo(() => {
    if (!dupSourceTables) return null
    const prefix = (t: string) => t.slice(0, t.lastIndexOf('_') + 1)
    const isSelected = (s: { dataset: string; table: string }) =>
      data.tables.some((t) => t.dataset === s.dataset && t.table === s.table)
    const added = data.tables.filter((t) => !dupSourceTables.some((s) => s.dataset === t.dataset && s.table === t.table))
    if (!added.length) return null // nothing new selected yet
    const pairs: Array<[string, string]> = []
    const unpaired: string[] = []
    for (const src of dupSourceTables.filter((s) => !isSelected(s))) {
      const matches = added.filter((t) => prefix(t.table) === prefix(src.table))
      if (matches.length !== 1) {
        unpaired.push(src.table)
        continue
      }
      pairs.push([src.table, matches[0].table])
      if (src.dataset !== matches[0].dataset && !pairs.some(([f]) => f === src.dataset)) {
        pairs.push([src.dataset, matches[0].dataset])
      }
    }
    const pattern = (name: string) => new RegExp(`\\b${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'g')
    const texts = [
      data.context,
      data.description,
      data.digest_snapshot_sql,
      ...data.premade_queries.map((q) => q.sql),
    ]
    const places = pairs.reduce(
      (n, [from]) => n + texts.reduce((m, txt) => m + (txt.match(pattern(from)) || []).length, 0),
      0
    )
    const rename = (txt: string) => pairs.reduce((s, [from, to]) => s.replace(pattern(from), to), txt)
    return { pairs, unpaired, places, rename }
  }, [dupSourceTables, data.tables, data.context, data.description, data.digest_snapshot_sql, data.premade_queries])

  const applyTableRenames = () => {
    if (!tableRenames) return
    const { rename } = tableRenames
    setData((d) => ({
      ...d,
      context: rename(d.context),
      description: rename(d.description),
      digest_snapshot_sql: rename(d.digest_snapshot_sql),
      premade_queries: d.premade_queries.map((q) => ({ ...q, sql: rename(q.sql), _testResult: null })),
    }))
    setDupSourceTables(null)
  }

  const gadsSafe = (data.google_ads_config?.read_only_mode ?? DEFAULT_GOOGLE_ADS_CONFIG.read_only_mode) !== false
  const gadsModeSummary = useMemo(() => {
    const names = new Map(gadsCampaigns.map((c) => [c.customer_id, c.account_name || c.customer_id]))
    const accounts = (data.google_ads_customer_ids || []).map((id) => ({ id, name: names.get(id) || id }))
    return summarizeMode(accounts, gadsAccess.access, gadsSafe)
  }, [data.google_ads_customer_ids, gadsCampaigns, gadsAccess.access, gadsSafe])
  const setGadsSafe = useCallback(
    (safe: boolean) =>
      setData((d) => ({
        ...d,
        google_ads_config: { ...DEFAULT_GOOGLE_ADS_CONFIG, ...(d.google_ads_config || {}), read_only_mode: safe },
      })),
    [],
  )

  useEffect(() => {
    const saved = localStorage.getItem('fm-theme') as 'dark' | 'light' | null
    if (saved) {
      setTheme(saved)
      document.documentElement.setAttribute('data-theme', saved)
    }

    // Check admin first (fast - don't block on data loading)
    ;(async () => {
      try {
        const roleRes = await api.get('/api/me/role')
        const canManage = !!roleRes.data?.can_manage_agents
        const adminStatus = !!roleRes.data?.is_admin
        setCanManageAgents(canManage)
        setIsAdmin(adminStatus)
      } catch (err: any) {
        // No fallback: if the role can't be read, the user gets no manager/admin UI.
        console.error('Failed to check permissions:', err)
        setCanManageAgents(false)
        setIsAdmin(false)
      } finally {
        setLoading(false) // Show page immediately after permission check
      }
    })()

    // If navigating via ?task=X URL, fetch agent data from API
    if (taskFromUrl && !editAgent) {
      ;(async () => {
        try {
          const res = await api.get('/api/datasets')
          const agents = res.data.agents || []
          const agent = agents.find((a: any) => a.task_name === taskFromUrl)
          if (agent) {
            console.log('Loaded agent from API:', agent)
            setEditAgent(agent)
          } else {
            console.error('Agent not found:', taskFromUrl)
          }
        } catch (err) {
          console.error('Failed to load agent:', err)
        }
      })()
    }

    // Load data in background (don't block rendering)
    ;(async () => {
      try {
        const res = await api.get('/api/tables')
        const list: string[] = res.data.projects || []
        setProjects(list)
        const project = data.bq_project || res.data.default_project || list[0] || ''
        if (project && project !== data.bq_project) {
          setData((d) => ({ ...d, bq_project: project }))
        }
        if (project) await loadCatalog(project)
      } catch {
        setError('Failed to load BigQuery projects')
      }
    })()
    
    // Load auxiliary data in parallel
    Promise.all([
      api.get('/api/slack/channels').then(ch => setChannels(ch.data.channels || [])).catch(() => {}),
      api.get('/api/groups').then(g => setPermGroups(g.data.groups || {})).catch(() => setPermGroups({})),
      api.get('/api/users').then(u => setAppUsers(u.data.users || [])).catch(() => setAppUsers([]))
    ])
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // When editAgent is loaded from API (via ?task= URL), update form data
  useEffect(() => {
    if (editAgent && taskFromUrl) {
      console.log('Updating form data from API-loaded agent:', editAgent)
      const initialSchedule = editAgent.schedule || '0 9 * * *'
      const hourMatch = /^0\s+(\d{1,2})\s+\*\s+\*\s+\*$/.exec(initialSchedule)
      
      // Convert old format to new format
      let tables: Array<{ dataset: string; table: string }> = []
      if (editAgent.bq_tables) {
        if (editAgent.bq_tables.length > 0 && typeof editAgent.bq_tables[0] === 'string') {
          const dataset = editAgent.bq_dataset || ''
          tables = editAgent.bq_tables.map((t: string) => {
            // Check if table name contains a dot (new multi-dataset format)
            if (t.includes('.')) {
              const parts = t.split('.')
              if (parts.length === 2) {
                // Format: "dataset.table"
                return { dataset: parts[0], table: parts[1] }
              } else if (parts.length === 3) {
                // Format: "project.dataset.table" (shouldn't happen but handle it)
                return { dataset: parts[1], table: parts[2] }
              }
            }
            // Old format: just table name, use agent's dataset
            return { dataset, table: t }
          })
        } else {
          tables = [...editAgent.bq_tables]
        }
      }
      
      // Infer skills from legacy config or use provided skills
      const skills = editAgent.skills?.length 
        ? [...editAgent.skills]
        : inferSkillsFromLegacy(editAgent)
      
      setData({
        task_name: editAgent.task_name || '',
        display_name: editAgent.display_name || '',
        description: editAgent.description || '',
        bq_project: editAgent.bq_project || '',
        bq_dataset: editAgent.bq_dataset || '',
        tables,
        context: editAgent.context || '',
        questions: editAgent.questions?.length ? [...editAgent.questions] : [''],
        allowed_users: editAgent.allowed_users ? [...editAgent.allowed_users] : [],
        premade_queries: (editAgent.premade_queries || []).map((pq: any) => ({
          name: pq.name || '',
          description: pq.description || '',
          sql: pq.sql || '',
          questions: (pq.questions || []).slice(),
          verified: !!pq.verified,
          _expanded: false,
          _testing: false,
          _testResult: null,
        })),
        digest_enabled: editAgent.digest_enabled !== false && !!(editAgent.schedule && String(editAgent.schedule).trim()),
        schedule_hour: hourMatch ? +hourMatch[1] : 9,
        custom_cron: !hourMatch && !!editAgent.schedule,
        schedule: initialSchedule,
        slack_channel: editAgent.slack_channel || '',
        slack_channel_id: editAgent.slack_channel_id || '',
        verticals: editAgent.slack_vertical ? [...editAgent.slack_vertical] : [],
        column_filters: { ...(editAgent.column_filters || {}) },
        column_value_filters: JSON.parse(JSON.stringify(editAgent.column_value_filters || {})),
        skills,
        digest_snapshot_sql: editAgent.digest_snapshot_sql || '',
        digest_template: editAgent.digest_template || '',
        digest_layout: editAgent.digest_layout ? [...editAgent.digest_layout] : [],
        data_lag_days: editAgent.data_lag_days ?? 3,
        max_days_behind: editAgent.max_days_behind ?? 4,
        google_ads_customer_ids: editAgent.google_ads_customer_ids ? [...editAgent.google_ads_customer_ids] : [],
        google_ads_campaign_ids: editAgent.google_ads_campaign_ids ? [...editAgent.google_ads_campaign_ids] : [],
        google_ads_config: editAgent.google_ads_config ? { ...editAgent.google_ads_config } : null,
        demand_pulse_datasets: editAgent.demand_pulse_datasets ? [...editAgent.demand_pulse_datasets] : [],
      })

      if (gadsCampaigns.length) {
        setGadsSelectedCampaignIds(savedCampaignSelection(gadsCampaigns, editAgent))
      }

      // Load catalog for the agent's project
      if (editAgent.bq_project) {
        loadCatalog(editAgent.bq_project)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editAgent, taskFromUrl])

  const loadCatalog = async (project: string) => {
    // Clear the old project's tables now; ignore responses for a project the user has left.
    catalogProject.current = project
    setCatalog([])
    setLoadingCatalog(true)
    try {
      const res = await api.get(`/api/tables?project=${encodeURIComponent(project)}`)
      if (catalogProject.current === project) setCatalog(res.data.datasets || [])
    } catch {
      if (catalogProject.current === project) setError('Failed to load datasets')
    } finally {
      if (catalogProject.current === project) setLoadingCatalog(false)
    }
  }

  // Digest SQL templates: loaded once the snapshot SQL editor is shown.
  const sqlMode = !!data.digest_snapshot_sql.trim()
  useEffect(() => {
    if (!sqlMode || sqlTemplates.length) return
    api
      .get(`/api/agents/${encodeURIComponent(data.task_name || '_test')}/digest-sql/templates`)
      .then((res) => {
        setSqlTemplates(res.data.templates || [])
        setMetricOptions(res.data.metrics || [])
        setPerfDef(res.data.performance || null)
        setSelectedTemplate((s) => s || data.digest_template)
      })
      .catch((err) => setDigestSqlTestResult({ error: extractErrorMessage(err, 'Could not load templates') }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sqlMode])
  // Tables a SQL uses that aren't selected above: project.dataset.table refs plus
  // bare FROM/JOIN names (CTE names excluded).
  const strayTables = (sql: string) => {
    const code = sql.replace(/'(?:[^'\\]|\\.)*'/g, "''").replace(/--.*$/gm, '')
    const selected = new Set(data.tables.map((t) => `${t.dataset}.${t.table}`))
    const names = new Set(data.tables.map((t) => t.table))
    const ctes = new Set([...code.matchAll(/\b(\w+)\s+AS\s*\(/gi)].map((m) => m[1].toLowerCase()))
    const out = new Set<string>()
    for (const m of code.matchAll(/`?([\w-]+)\.(\w+)\.(\w+)`?/g)) {
      if (m[1] !== data.bq_project || !selected.has(`${m[2]}.${m[3]}`)) out.add(`${m[1]}.${m[2]}.${m[3]}`)
    }
    for (const m of code.matchAll(/\b(?:FROM|JOIN)\s+`?([A-Za-z_][\w-]*)`?(?![\w.`-])/gi)) {
      if (!names.has(m[1]) && !ctes.has(m[1].toLowerCase())) out.add(m[1])
    }
    return [...out].sort()
  }
  // Slack detail tables for this agent: its own order if set, else the template's default.
  // Agents saved before templates were recorded use Performance Query's tables.
  // Hand-written SQL (no template recorded) can use any table; it still defaults to
  // Performance Query's tables, which is what the engine shows without a layout.
  const defaultTemplate = sqlTemplates.find((t) => t.id === 'performance_query')
  const layoutTemplate: SqlTemplate | null = data.digest_template
    ? sqlTemplates.find((t) => t.id === data.digest_template) || null
    : defaultTemplate
      ? {
          ...defaultTemplate,
          name: 'all available tables',
          layout: sqlTemplates
            .flatMap((t) => t.layout)
            .filter((x, i, all) => all.findIndex((y) => y.id === x.id) === i),
        }
      : null
  const layoutRows: LayoutRow[] = (() => {
    if (!layoutTemplate) return []
    const all = layoutTemplate.layout
    const ids = all.map((x) => x.id)
    const custom: Record<string, string[]> = {}
    for (const x of data.digest_layout) if (typeof x !== 'string' && x.metrics?.length) custom[x.id] = x.metrics
    const defaults = (data.digest_template ? all : defaultTemplate?.layout || []).filter((x) => x.default).map((x) => x.id)
    const saved = data.digest_layout.map(layoutId).filter((i) => ids.includes(i))
    const on = saved.length ? saved : defaults
    return [...on, ...ids.filter((i) => !on.includes(i))].map((id) => {
      const t = all.find((x) => x.id === id)!
      return { id, heading: t.heading, dim: t.dim, defaults: t.metrics, metrics: custom[id] || t.metrics, on: on.includes(id) }
    })
  })()
  // Message 1's Performance table: always shown; saved only when its columns are changed.
  // Its default count column follows the data (clickouts when there are no conversions).
  const performanceRow: LayoutRow | null = perfDef
    ? (() => {
        const defaults = metricCatalog?.find((c) => c.id === perfDef.id)?.defaults || perfDef.metrics
        const saved = data.digest_layout.find((x) => typeof x !== 'string' && x.id === perfDef.id)
        const metrics = saved && typeof saved !== 'string' && saved.metrics.length ? saved.metrics : defaults
        return { id: perfDef.id, heading: perfDef.heading, dim: perfDef.dim, on: true, metrics, defaults }
      })()
    : null
  const performanceItem = (row: LayoutRow | null): LayoutItem[] =>
    row && row.metrics.join() !== row.defaults.join() ? [{ id: row.id, metrics: row.metrics }] : []
  // Tables with default columns are saved as a bare id, so they follow the template.
  const setLayout = (rows: LayoutRow[]) =>
    setData((d) => ({
      ...d,
      digest_layout: [
        ...performanceItem(performanceRow),
        ...rows
          .filter((r) => r.on)
          .map((r) => (r.metrics.join() === r.defaults.join() ? r.id : { id: r.id, metrics: r.metrics })),
      ],
    }))
  const setPerformance = (row: LayoutRow) =>
    setData((d) => ({
      ...d,
      digest_layout: [...performanceItem(row), ...d.digest_layout.filter((x) => layoutId(x) !== row.id)],
    }))
  const testSnapshotSql = async () => {
    setDigestSqlTesting(true)
    setDigestSqlTestResult(null)
    try {
      const res = await api.post(`/api/agents/${encodeURIComponent(data.task_name || '_test')}/digest-sql/test`, {
        sql: data.digest_snapshot_sql,
        bq_project: data.bq_project,
        bq_dataset: data.bq_dataset,
      })
      setSqlTestPopup(res.data)
    } catch (err: any) {
      setDigestSqlTestResult({ error: extractErrorMessage(err, 'Query failed') })
    } finally {
      setDigestSqlTesting(false)
    }
  }
  const previewSlack = async () => {
    setPreviewing(true)
    setDigestSqlTestResult(null)
    try {
      const res = await api.post(`/api/agents/${encodeURIComponent(data.task_name || '_test')}/digest-sql/preview`, {
        sql: data.digest_snapshot_sql,
        bq_project: data.bq_project,
        bq_dataset: data.bq_dataset,
        display_name: data.display_name,
        digest_template: data.digest_template,
        digest_layout: data.digest_layout,
        data_lag_days: data.data_lag_days,
      })
      setSlackPreview(res.data)
      setMetricCatalog(res.data.catalog)
      setCatalogSql(data.digest_snapshot_sql)
      setCatalogStatus({ state: 'ready', analysisDate: res.data.analysis_date, stale: false })
    } catch (err: any) {
      setDigestSqlTestResult({ error: extractErrorMessage(err, 'Preview failed') })
    } finally {
      setPreviewing(false)
    }
  }
  // Which metrics each table can show, read from the snapshot SQL's real output.
  const loadMetricCatalog = async () => {
    const sql = data.digest_snapshot_sql
    if (!sql.trim() || !data.bq_project) return
    setCatalogStatus({ state: 'loading' })
    try {
      const res = await api.post(`/api/agents/${encodeURIComponent(data.task_name || '_test')}/digest-sql/metrics`, {
        sql,
        bq_project: data.bq_project,
        bq_dataset: data.bq_dataset,
        data_lag_days: data.data_lag_days,
      })
      setMetricCatalog(res.data.catalog)
      setMetricOptions(res.data.metrics)
      setCatalogSql(sql)
      setCatalogStatus({ state: 'ready', analysisDate: res.data.analysis_date, stale: false })
    } catch (err: any) {
      setMetricCatalog(null)
      setCatalogStatus({ state: 'error', message: extractErrorMessage(err, 'Could not read metrics from the SQL.') })
    }
  }
  const shownCatalogStatus: CatalogStatus =
    catalogStatus.state === 'ready' ? { ...catalogStatus, stale: catalogSql !== data.digest_snapshot_sql } : catalogStatus
  const missingTables = (tpl: SqlTemplate) =>
    tpl.requires.filter((prefix) => !data.tables.some((t) => t.table.startsWith(prefix)))

  const mergedCatalog = useMemo(() => {
    let groups = catalog.map((g) => ({ dataset: g.dataset, tables: [...g.tables] }))
    
    // Add any datasets/tables from current selection that aren't in catalog
    const datasetsInSelection = new Set(data.tables.map(t => t.dataset))
    datasetsInSelection.forEach(ds => {
      if (!ds) return
      let g = groups.find((x) => x.dataset === ds)
      if (!g) {
        const tablesInDs = data.tables.filter(t => t.dataset === ds).map(t => t.table)
        g = { dataset: ds, tables: [...tablesInDs].sort() }
        groups.push(g)
      } else {
        data.tables.filter(t => t.dataset === ds).forEach(({table}) => {
          if (!g!.tables.includes(table)) g!.tables.push(table)
        })
        g.tables.sort()
      }
    })
    
    return groups
  }, [catalog, data.tables])

  // Auto-collapse all datasets once catalog is known
  useEffect(() => {
    if (!mergedCatalog.length) return
    setCollapsedDatasets((prev) => {
      const next = { ...prev }
      let changed = false
      mergedCatalog.forEach((g) => {
        if (!(g.dataset in next)) {
          // Collapse all datasets by default
          next[g.dataset] = true
          changed = true
        }
      })
      return changed ? next : prev
    })
  }, [mergedCatalog])

  // First load selects the agent's saved campaigns; later reloads keep the current selection.
  const loadGadsCampaigns = (initial: boolean) => {
    setGadsCampaignsLoading(true)
    api
      .get('/api/google-ads/campaigns')
      .then((res) => {
        const fetched: GAdsCampaign[] = res.data || []
        setGadsCampaignsError('')
        setGadsCampaigns(fetched)
        if (initial) setGadsSelectedCampaignIds(savedCampaignSelection(fetched, editAgent ?? data))
        else setGadsSelectedCampaignIds((prev) => new Set(fetched.filter((c) => prev.has(c.campaign_id)).map((c) => c.campaign_id)))
      })
      .catch((err) => {
        const msg = err?.response?.data?.detail || err?.message || 'Failed to load campaigns'
        setGadsCampaignsError(msg)
      })
      .finally(() => {
        setGadsCampaignsLoading(false)
        setGadsLoadedOnce(true)
      })
  }

  const refreshGoogleAds = () => {
    loadGadsCampaigns(false)
    gadsAccess.recheckAll()
  }

  // Reload accounts and recheck access every time the Configure step is opened, so access an
  // advertiser changed in Google Ads shows up without reloading the page.
  useEffect(() => {
    if (step !== 3 || !data.skills.includes('google_ads') || gadsCampaignsLoading) return
    if (!gadsFetched) {
      setGadsFetched(true)
      loadGadsCampaigns(true)
    } else {
      refreshGoogleAds()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, data.skills])

  // Demand Pulse: datasets this agent can use. The first load pre-selects the
  // recommended ones (matched on the agent's name) unless the agent already has a selection.
  const [dpDatasets, setDpDatasets] = useState<DemandPulseDataset[]>([])
  const [dpLoading, setDpLoading] = useState(false)
  const [dpError, setDpError] = useState('')
  useEffect(() => {
    if (step !== 3 || !data.skills.includes('demand_pulse')) return
    setDpLoading(true)
    setDpError('')
    api
      .get('/api/demand-pulse/datasets', { params: { agent: `${data.display_name} ${data.task_name}` } })
      .then((res) => {
        const list: DemandPulseDataset[] = res.data?.datasets || []
        setDpDatasets(list)
        setData((d) =>
          d.demand_pulse_datasets.length
            ? d
            : { ...d, demand_pulse_datasets: list.filter((x) => x.recommended).map((x) => x.id) },
        )
      })
      .catch((err) => setDpError(err?.response?.data?.detail || "Couldn't load Demand Pulse datasets."))
      .finally(() => setDpLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, data.skills])

  const toggleDemandPulseDataset = (id: string, on: boolean) =>
    setData((d) => ({
      ...d,
      demand_pulse_datasets: on
        ? [...new Set([...d.demand_pulse_datasets, id])]
        : d.demand_pulse_datasets.filter((x) => x !== id),
    }))

  const toggleTheme = () => {
    const next = theme === 'light' ? 'dark' : 'light'
    setTheme(next)
    localStorage.setItem('fm-theme', next)
    document.documentElement.setAttribute('data-theme', next)
  }

  const handleProjectChange = async (project: string) => {
    setData((d) => ({
      ...d,
      bq_project: project,
      bq_dataset: '',
      tables: [],
      column_filters: {},
      column_value_filters: {},
    }))
    setExpandedTables({})
    setCollapsedDatasets({})
    setSchemaCache({})
    if (project) await loadCatalog(project)
  }

  const openColumnPanel = async (table: string, dataset?: string) => {
    const ds = dataset || data.bq_dataset
    const project = data.bq_project
    setExpandedTables((e) => ({ ...e, [table]: true }))
    if (schemaCache[table] || schemaLoading[table]) return
    if (!project || !ds) return
    setSchemaLoading((s) => ({ ...s, [table]: true }))
    setSchemaError((s) => {
      const n = { ...s }
      delete n[table]
      return n
    })
    try {
      const res = await api.get(
        `/api/tables/schema?project=${encodeURIComponent(project)}&dataset=${encodeURIComponent(ds)}&table=${encodeURIComponent(table)}`
      )
      setSchemaCache((c) => ({ ...c, [table]: res.data }))
    } catch (err: any) {
      setSchemaError((s) => ({
        ...s,
        [table]: extractErrorMessage(err, 'Could not load schema'),
      }))
    } finally {
      setSchemaLoading((s) => ({ ...s, [table]: false }))
    }
  }

  const toggleTable = (ds: string, table: string, on: boolean) => {
    setData((d) => {
      if (on) {
        const exists = d.tables.some(t => t.dataset === ds && t.table === table)
        if (exists) return d
        return { 
          ...d, 
          bq_dataset: d.bq_dataset || ds,  // Set first dataset as default for backward compat
          tables: [...d.tables, { dataset: ds, table }] 
        }
      }
      const tables = d.tables.filter((t) => !(t.dataset === ds && t.table === table))
      const column_filters = { ...d.column_filters }
      const column_value_filters = { ...d.column_value_filters }
      delete column_filters[table]
      delete column_value_filters[table]
      return {
        ...d,
        tables,
        bq_dataset: tables.length ? d.bq_dataset : '',
        column_filters,
        column_value_filters,
      }
    })
    if (on) {
      setCollapsedDatasets((c) => ({ ...c, [ds]: false }))
    } else {
      setExpandedTables((e) => {
        const n = { ...e }
        delete n[table]
        return n
      })
    }
  }

  const selectAllInDataset = (ds: string) => {
    const group = mergedCatalog.find((g) => g.dataset === ds)
    if (!group) return
    
    const tablesInDs = data.tables.filter(t => t.dataset === ds)
    const allSelected = group.tables.every((t) => tablesInDs.some(dt => dt.table === t))

    if (allSelected) {
      // Deselect all from this dataset
      setData((d) => ({
        ...d,
        tables: d.tables.filter(t => t.dataset !== ds),
        bq_dataset: d.tables.filter(t => t.dataset !== ds).length ? d.bq_dataset : '',
        column_filters: {},
        column_value_filters: {},
      }))
      setExpandedTables({})
    } else {
      // Select all from this dataset
      setData((d) => {
        const otherTables = d.tables.filter(t => t.dataset !== ds)
        const newTables = group.tables.map(table => ({ dataset: ds, table }))
        return { 
          ...d, 
          bq_dataset: d.bq_dataset || ds,
          tables: [...otherTables, ...newTables] 
        }
      })
      setCollapsedDatasets((c) => ({ ...c, [ds]: false }))
    }
  }

  const toggleColumn = (table: string, col: string, on: boolean) => {
    const schema = schemaCache[table]
    const allCols = schema ? schema.columns.map((c) => c.name) : []
    setData((d) => {
      let current = d.column_filters[table] ? [...d.column_filters[table]] : [...allCols]
      if (on) {
        if (!current.includes(col)) current.push(col)
      } else {
        current = current.filter((c) => c !== col)
      }
      const column_filters = { ...d.column_filters }
      if (current.length === allCols.length && allCols.every((c) => current.includes(c))) {
        delete column_filters[table]
      } else {
        column_filters[table] = current
      }
      return { ...d, column_filters }
    })
  }

  const selectAllColumns = (table: string) => {
    setData((d) => {
      const column_filters = { ...d.column_filters }
      delete column_filters[table]
      return { ...d, column_filters }
    })
  }

  const selectNoneColumns = (table: string) => {
    setData((d) => ({ ...d, column_filters: { ...d.column_filters, [table]: [] } }))
  }

  const addRowFilter = (table: string, filter: RowFilter) => {
    setData((d) => ({
      ...d,
      column_value_filters: {
        ...d.column_value_filters,
        [table]: [...(d.column_value_filters[table] || []), filter],
      },
    }))
  }

  const removeRowFilter = (table: string, index: number) => {
    setData((d) => {
      const list = [...(d.column_value_filters[table] || [])]
      list.splice(index, 1)
      const column_value_filters = { ...d.column_value_filters }
      if (list.length) column_value_filters[table] = list
      else delete column_value_filters[table]
      return { ...d, column_value_filters }
    })
  }

  const colBadge = (table: string) => {
    const schema = schemaCache[table]
    if (!schema) return { text: '… cols', cls: 'loading', title: 'Expand to load schema' }
    const total = schema.columns.length
    const filter = data.column_filters[table]
    const selected = filter ? filter.length : total
    return {
      text: `${selected}/${total} cols`,
      cls: filter && selected < total ? 'filtered' : '',
      title: selected === total ? 'All columns visible to agent' : `${total - selected} column(s) hidden from agent`,
    }
  }

  const validateStep = (n: number): boolean => {
    setError('')
    // Step 1: Agent Details - require display name
    if (n === 1 && data.display_name.trim().length < 2) {
      setError('Display name needs at least 2 characters.')
      return false
    }
    // Step 2: Select Skills - always valid (bigquery is required by default)
    if (n === 2) {
      return true
    }
    // Step 3: Configure Skills - validate based on selected skills
    if (n === 3) {
      if (data.skills.includes('bigquery') && !data.tables.length) {
        setError('BigQuery skill requires at least one table.')
        return false
      }
      if (data.skills.includes('demand_pulse') && !data.demand_pulse_datasets.length) {
        setError('Select at least one Demand Pulse dataset, or turn the Demand Pulse skill off.')
        return false
      }
      // Digest runs either the snapshot SQL or the questions, never both
      if (data.skills.includes('slack') && data.digest_enabled && data.digest_snapshot_sql.trim()) {
        if (!/\bselect\b/i.test(data.digest_snapshot_sql.replace(/--.*$/gm, ''))) {
          setError('Digest snapshot SQL is empty. Write the query, or switch back to digest questions.')
          return false
        }
        const stray = strayTables(data.digest_snapshot_sql)
        if (stray.length) {
          setError(
            `Digest snapshot SQL uses ${stray.join(', ')}, which ${stray.length === 1 ? 'is' : 'are'} not in the ` +
              'selected tables. Generate it again from a template or fix the table names.'
          )
          return false
        }
      } else if (data.skills.includes('slack') && data.digest_enabled) {
        const qs = data.questions.map((q) => q.trim()).filter(Boolean)
        if (!qs.length) {
          setError('Slack digest needs at least one question, or turn the digest off.')
          return false
        }
      }
    }
    return true
  }

  const submit = async () => {
    if (!validateStep(3)) return
    setBusy('save')
    setError('')
    try {
      const schedule = data.digest_enabled
        ? data.custom_cron
          ? data.schedule
          : `0 ${data.schedule_hour} * * *`
        : ''

      // Backward-compatible format
      const datasets = Array.from(new Set(data.tables.map(t => t.dataset)))
      const isSingleDataset = datasets.length === 1

      const payload: any = {
        // CRITICAL: When editing, task_name must stay fixed!
        task_name: isEditMode ? data.task_name : '',
        display_name: data.display_name.trim(),
        description: data.description.trim(),
        bq_project: data.bq_project,
        context: data.context,
        questions: data.questions.map((q) => q.trim()).filter(Boolean),
        allowed_users: data.allowed_users,
        premade_queries: data.premade_queries
          .filter((pq) => pq.name.trim() && pq.sql.trim().length >= 10)
          .map((pq) => ({
            name: pq.name.trim(),
            description: pq.description,
            sql: pq.sql,
            questions: pq.questions,
            verified: pq.verified,
          })),
        digest_enabled: data.digest_enabled,
        schedule,
        slack_channel: data.slack_channel,
        slack_channel_id: data.slack_channel_id,
        slack_vertical: data.verticals,
        column_filters: data.column_filters,
        column_value_filters: data.column_value_filters,
        skills: data.skills,
        digest_snapshot_sql: data.digest_snapshot_sql || '',
        digest_template: data.digest_template || '',
        digest_layout: data.digest_layout,
        data_lag_days: data.data_lag_days ?? 3,
        max_days_behind: data.max_days_behind ?? 4,
        google_ads_customer_ids: data.google_ads_customer_ids || [],
        google_ads_campaign_ids: data.google_ads_campaign_ids || [],
        google_ads_config: data.skills.includes('google_ads')
          ? { ...DEFAULT_GOOGLE_ADS_CONFIG, ...(data.google_ads_config || {}) }
          : null,
        demand_pulse_datasets: data.skills.includes('demand_pulse') ? data.demand_pulse_datasets : [],
      }

      if (isSingleDataset) {
        // Old format: single dataset
        let ds = datasets[0]
        // Safety: if dataset somehow contains project prefix, strip it
        if (ds && ds.includes('.') && ds.startsWith(data.bq_project + '.')) {
          ds = ds.substring(data.bq_project.length + 1)
        }
        payload.bq_dataset = ds
        payload.bq_tables = data.tables.map(t => t.table)
      } else {
        // New format: multiple datasets - use fully qualified names
        let primaryDs = datasets[0]
        // Safety: strip project prefix if present
        if (primaryDs && primaryDs.includes('.') && primaryDs.startsWith(data.bq_project + '.')) {
          primaryDs = primaryDs.substring(data.bq_project.length + 1)
        }
        payload.bq_dataset = primaryDs // Primary dataset for backward compat
        
        // Construct dataset.table format, stripping any project prefix
        payload.bq_tables = data.tables.map(t => {
          let ds = t.dataset
          // Safety: if dataset contains project prefix, strip it
          if (ds && ds.includes('.') && ds.startsWith(data.bq_project + '.')) {
            ds = ds.substring(data.bq_project.length + 1)
          }
          return `${ds}.${t.table}`
        })
      }

      await api.post('/api/datasets', payload)
      
      // Show success toast
      setShowToast({ message: 'Agent saved successfully!', type: 'success' })
      
      // Navigate back after a brief delay to show the toast
      setTimeout(() => {
        navigate(-1)
      }, 1500)
    } catch (err: any) {
      const errorMsg = extractErrorMessage(err, `Failed to ${isEditMode ? 'update' : 'create'} agent`)
      setError(errorMsg)
      setShowToast({ message: errorMsg, type: 'error' })
    } finally {
      setBusy('')
    }
  }

  const needle = tableSearch.trim().toLowerCase()
  const cronPreview = !data.digest_enabled
    ? 'Digest disabled — the agent stays chat-only.'
    : (() => {
        const m = /^0\s+(\d{1,2})\s+\*\s+\*\s+\*$/.exec(data.schedule.trim())
        if (!data.custom_cron) return `Runs every day at ${String(data.schedule_hour).padStart(2, '0')}:00 UTC.`
        if (m) return `Runs every day at ${String(+m[1]).padStart(2, '0')}:00 UTC.`
        if (/^\s*\S+\s+\S+\s+\S+\s+\S+\s+\S+\s*$/.test(data.schedule)) return `Custom cron: ${data.schedule.trim()} (UTC)`
        return 'Invalid cron — needs 5 fields (min hour day month weekday).'
      })()

  // Show loading state
  if (loading) {
    return (
      <div style={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)', color: 'var(--text)', colorScheme: theme }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'center' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '48px', height: '48px', color: 'var(--accent)', animation: 'spin 1s linear infinite' }}>
              <path d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48l2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48l2.83-2.83" strokeLinecap="round"/>
            </svg>
          </div>
          <p style={{ fontSize: '14px', color: 'var(--text-muted)' }}>Loading...</p>
        </div>
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    )
  }

  if (!canManageAgents) {
    return (
      <div style={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)', color: 'var(--text)', colorScheme: theme }}>
        <div style={{ textAlign: 'center', maxWidth: '400px', padding: '40px' }}>
          <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'center' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ width: '80px', height: '80px', color: 'var(--text-muted)', opacity: 0.6 }}>
              <rect x="5" y="11" width="14" height="10" rx="2" />
              <path d="M12 17v-2" />
              <circle cx="12" cy="17" r="0.5" fill="currentColor" />
              <path d="M8 11V7a4 4 0 0 1 8 0v4" />
            </svg>
          </div>
          <h2 style={{ fontSize: '24px', fontWeight: 600, marginBottom: '12px' }}>Permission Required</h2>
          <p style={{ fontSize: '14px', color: 'var(--text-muted)', marginBottom: '24px', lineHeight: '1.6' }}>
            You need admin permissions to create or edit Data Agents. Please contact an administrator if you need access.
          </p>
          <button
            type="button"
            onClick={() => navigate('/')}
            style={{
              padding: '10px 20px',
              background: 'var(--accent)',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'opacity 0.15s'
            }}
            onMouseOver={(e) => { e.currentTarget.style.opacity = '0.85'; }}
            onMouseOut={(e) => { e.currentTarget.style.opacity = '1'; }}
          >
            Back to Home
          </button>
        </div>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', background: 'var(--bg)', color: 'var(--text)', colorScheme: theme }} className="aa-root">
      {/* Toast notification */}
      {showToast && (
        <div
          onClick={() => setShowToast(null)}
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            zIndex: 9999,
            padding: '12px 20px',
            borderRadius: '8px',
            background: showToast.type === 'success' ? '#10b981' : '#ef4444',
            color: '#fff',
            fontSize: '14px',
            fontWeight: 500,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            animation: 'slideIn 0.3s ease-out',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            cursor: 'pointer'
          }}
          title="Click to dismiss"
        >
          <span>{showToast.type === 'success' ? '✓' : '✕'}</span>
          {showToast.message}
        </div>
      )}
      
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)}>
        <button type="button" className="st-btn" style={{ width: '100%', textAlign: 'left' }} onClick={() => navigate(-1)}>
          ← Back
        </button>
      </Sidebar>

      <main style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <TopBar
          sidebarOpen={sidebarOpen}
          onToggleSidebar={() => setSidebarOpen(true)}
          theme={theme}
          onToggleTheme={toggleTheme}
          user={user}
        />

        <div className="aa-page">
          <div className="aa-scroll">
            <div className={`aa-content${step === 2 ? '' : ' narrow'}`}>
              <div className="st-section-title">
                {isEditMode ? `Edit Data Agent — ${data.display_name || data.task_name}` : 'New Data Agent'}
              </div>
              {isEditMode && (
                <div className="st-section-sub">
                  task_name: <code>{data.task_name}</code> (fixed)
                </div>
              )}
              {!isEditMode && <div className="st-section-sub">
                {step === 1 && 'Set the agent name, description, and access permissions.'}
                {step === 2 && 'Select which capabilities this agent should have.'}
                {step === 3 && 'Configure the selected skills.'}
              </div>}

              <div className="st-steps">
                {['Agent Details', 'Select Skills', 'Configure'].map((name, i) => {
                  const n = i + 1
                  const cls = n === step ? 'active' : n < step ? 'done' : ''
                  return (
                    <span key={name} style={{ display: 'contents' }}>
                      <span className={`st-step ${cls}`}>
                        <span className="n">{n < step ? '✓' : n}</span>
                        {name}
                      </span>
                      {n < 3 && <span className="st-step-line" />}
                    </span>
                  )
                })}
              </div>

              {sqlTestPopup && (
                <div className="gads-dialog-backdrop" onClick={() => setSqlTestPopup(null)}>
                  <div
                    className="gads-dialog"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="sql-test-title"
                    style={{ maxWidth: 900, maxHeight: '85vh', overflowY: 'auto' }}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.key === 'Escape' && setSqlTestPopup(null)}
                  >
                    <h3 id="sql-test-title" style={{ marginTop: 0 }}>Test query — snapshot SQL</h3>
                    <div className="st-hint" style={{ marginBottom: 14 }}>
                      The digest snapshot SQL ran against live data. Nothing was saved or posted.
                    </div>
                    <div className="sl-test-stats">
                      <div>
                        <span className="st-hint">Analysis date</span>
                        <strong>{sqlTestPopup.analysis_date}</strong>
                      </div>
                      <div>
                        <span className="st-hint">Rows</span>
                        <strong>{(sqlTestPopup.total_rows || 0).toLocaleString()}</strong>
                      </div>
                      <div>
                        <span className="st-hint">Scanned</span>
                        <strong>{((sqlTestPopup.bytes_processed || 0) / 1e9).toFixed(2)} GB</strong>
                      </div>
                    </div>
                    {sqlTestPopup.sections && (
                      <div style={{ marginBottom: 14 }}>
                        <div style={{ fontWeight: 600, marginBottom: 6 }}>
                          Sections ({Object.keys(sqlTestPopup.sections).length})
                        </div>
                        <div className="sl-chips">
                          {Object.entries(sqlTestPopup.sections as Record<string, number>)
                            .sort(([a], [b]) => a.localeCompare(b))
                            .map(([section, count]) => (
                              <span key={section} className="st-col-badge">
                                {section} · {count}
                              </span>
                            ))}
                        </div>
                      </div>
                    )}
                    {sqlTestPopup.sample_rows?.length > 0 && (
                      <>
                        <div style={{ fontWeight: 600, marginBottom: 6 }}>Sample rows</div>
                        <ResultTable
                          columns={Object.keys(sqlTestPopup.sample_rows[0])}
                          rows={sqlTestPopup.sample_rows.slice(0, 10).map((row: any) => {
                            const formatted: Record<string, string> = {}
                            for (const [key, value] of Object.entries(row)) {
                              formatted[key] = value == null ? '' : String(value)
                            }
                            return formatted
                          })}
                        />
                        {sqlTestPopup.sample_rows.length > 10 && (
                          <div className="st-hint" style={{ marginTop: 8 }}>
                            Showing the first 10 of {sqlTestPopup.sample_rows.length} sample rows
                          </div>
                        )}
                      </>
                    )}
                    <div className="gads-dialog-actions">
                      <button type="button" className="st-btn primary" autoFocus onClick={() => setSqlTestPopup(null)}>
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {slackPreview && (
                <div className="gads-dialog-backdrop" onClick={() => setSlackPreview(null)}>
                  <div
                    className="gads-dialog"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="slack-preview-title"
                    style={{ maxWidth: 900, maxHeight: '85vh', overflowY: 'auto' }}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.key === 'Escape' && setSlackPreview(null)}
                  >
                    <h3 id="slack-preview-title" style={{ marginTop: 0 }}>Slack preview — not posted</h3>
                    <div className="st-hint" style={{ marginBottom: 14 }}>
                      Built from live data for {slackPreview.analysis_date} (today minus the data lag). The scheduled
                      digest picks its date from the freshest data, so numbers can differ slightly.
                    </div>

                    <div className="st-premade-result" style={{ marginBottom: 16 }}>
                      <div style={{ fontSize: 16, fontWeight: 700 }}>{slackPreview.title} Daily Briefing</div>
                      <div className="st-hint" style={{ marginBottom: 10 }}>
                        Data as of: {slackPreview.analysis_date}
                      </div>
                      <div style={{ fontWeight: 600, marginBottom: 6 }}>Performance</div>
                      <PreviewTable table={slackPreview.performance} />
                      <div className="st-hint">
                        The status line, primary driver, key insights and actions are written by the AI when the
                        digest runs, from these tables.
                      </div>
                    </div>

                    <div className="st-premade-result">
                      <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 10 }}>
                        {slackPreview.title} Detail
                      </div>
                      {slackPreview.sections.length ? (
                        slackPreview.sections.map((sec) => (
                          <div key={sec.heading}>
                            <div style={{ fontWeight: 600, marginBottom: 6 }}>
                              {sec.heading}
                            </div>
                            <PreviewTable table={sec} />
                          </div>
                        ))
                      ) : (
                        <div className="st-hint">No detail tables: every table is turned off or has no rows.</div>
                      )}
                    </div>

                    <div className="gads-dialog-actions">
                      <button type="button" className="st-btn primary" autoFocus onClick={() => setSlackPreview(null)}>
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {error && (
                <div className="gads-dialog-backdrop" onClick={() => setError('')}>
                  <div
                    className="gads-dialog"
                    role="alertdialog"
                    aria-modal="true"
                    aria-labelledby="agent-error-title"
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.key === 'Escape' && setError('')}
                  >
                    <h3 id="agent-error-title" style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 0 }}>
                      <AlertTriangle size={18} color="var(--red)" aria-hidden /> Can&apos;t continue yet
                    </h3>
                    <p style={{ lineHeight: 1.5, overflowWrap: 'anywhere' }}>{error}</p>
                    <div className="gads-dialog-actions">
                      <button type="button" className="st-btn primary" autoFocus onClick={() => setError('')}>
                        OK
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Step 1: Agent Details ── */}
              {step === 1 && (
                <>
                  <div className="st-field">
                    <label style={{ fontSize: '15px', fontWeight: 600, marginBottom: 8 }}>
                      Agent name <span className="req">*</span>
                    </label>
                    <input
                      type="text"
                      maxLength={80}
                      value={data.display_name}
                      placeholder="Enter agent name..."
                      onChange={(e) => setData((d) => ({ ...d, display_name: e.target.value }))}
                      style={{ fontSize: '15px', padding: '12px' }}
                    />
                  </div>

                  <div className="st-field">
                    <label>Description</label>
                    <input
                      type="text"
                      maxLength={200}
                      value={data.description}
                      placeholder="A brief summary of what this agent does"
                      onChange={(e) => setData((d) => ({ ...d, description: e.target.value }))}
                    />
                  </div>

                  <div className="st-field">
                    <div className="st-ctx-header">
                      <label style={{ margin: 0 }}>Instructions</label>
                      <button type="button" className="st-ctx-toggle" onClick={() => setShowCtxEditor(true)}>
                        ⤢ Expand editor
                      </button>
                    </div>
                    <textarea
                      rows={10}
                      value={data.context}
                      placeholder="Describe what this data tracks, key metrics, table roles, and any important join rules."
                      onChange={(e) => setData((d) => ({ ...d, context: e.target.value }))}
                      style={{ minHeight: 220 }}
                    />
                  </div>

                  <div className="st-field">
                    <label>Access control</label>
                    <div className="st-hint" style={{ marginTop: -4, marginBottom: 8 }}>
                      Specify who can use this agent
                    </div>
                    <ChipInput
                      values={data.allowed_users}
                      onChange={(allowed_users) => setData((d) => ({ ...d, allowed_users }))}
                      placeholder="Type to search groups or users…"
                      suggestions={[
                        ...Object.keys(permGroups).map((name) => ({
                          value: `group:${name}`,
                          label: `group:${name}`,
                          badge: 'Group',
                          badgeCls: 'info',
                        })),
                        ...appUsers.map((u) => ({
                          value: u.email,
                          label: u.first_name && u.last_name ? `${u.first_name} ${u.last_name} (${u.email})` : u.email,
                          badge: 'User',
                          badgeCls: 'ok',
                        })),
                      ]}
                    />
                  </div>
                </>
              )}

              {/* ── Step 2: Select Skills ── */}
              {step === 2 && (
                <>
                  <div className="st-field">
                    <label style={{ fontSize: '15px', fontWeight: 600, marginBottom: 12 }}>
                      Select Agent Capabilities
                    </label>
                    <div className="st-hint" style={{ marginBottom: 20 }}>
                      Choose which skills this agent should have. You'll configure them in the next step.
                    </div>
                    
                    <SkillSelector
                      skills={getAvailableSkills(isAdmin)}
                      selected={data.skills}
                      onChange={(skills) => setData(d => ({ ...d, skills }))}
                    />
                  </div>
                </>
              )}

              {/* ── Step 3: Configure Skills ── */}
              {step === 3 && (
                <>
                  {/* BigQuery Configuration */}
                  {data.skills.includes('bigquery') && (
                    <div className="st-skill-config-section">
                      <h3 
                        className="st-skill-config-title"
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => setBqSkillExpanded(!bqSkillExpanded)}
                      >
                        <span style={{ marginRight: 8 }}>{bqSkillExpanded ? '▼' : '▶'}</span>
                        <Database size={24} strokeWidth={2} />
                        BigQuery Configuration
                      </h3>
                {bqSkillExpanded && (
                <>
                  <div className="st-field">
                    <label>
                      GCP project <span className="st-yaml-key">bq_project</span>
                    </label>
                    <select value={data.bq_project} onChange={(e) => handleProjectChange(e.target.value)}>
                      <option value="">Select project…</option>
                      {projects.map((p) => (
                        <option key={p} value={p}>
                          {p}
                        </option>
                      ))}
                      {data.bq_project && !projects.includes(data.bq_project) && (
                        <option value={data.bq_project}>{data.bq_project}</option>
                      )}
                    </select>
                    <div className="st-hint">
                      Tables shown are those the agent&apos;s service account can read. An agent can access tables from multiple datasets.
                    </div>
                  </div>

                  <div className="st-field">
                    <input
                      type="search"
                      placeholder="Search tables…"
                      value={tableSearch}
                      onChange={(e) => setTableSearch(e.target.value)}
                    />
                  </div>

                  {loadingCatalog && data.tables.length > 0 && (
                    <div className="st-hint st-loading" style={{ marginBottom: 8 }}>
                      <Loader2 size={14} className="st-spin" aria-hidden />
                      Loading the full table list… your current selection is shown below.
                    </div>
                  )}

                  {!data.bq_project ? (
                    <div className="st-empty">Select a project</div>
                  ) : loadingCatalog && !mergedCatalog.length ? (
                    <div className="st-empty st-loading">
                      <Loader2 size={16} className="st-spin" aria-hidden />
                      Loading tables…
                    </div>
                  ) : !mergedCatalog.length ? (
                    <div className="st-empty">No accessible datasets in this project.</div>
                  ) : (
                    <div className="st-table-picker">
                      {mergedCatalog.map((group) => {
                        const tables = group.tables.filter(
                          (t) => !needle || t.toLowerCase().includes(needle) || group.dataset.toLowerCase().includes(needle)
                        )
                        if (!tables.length) return null
                        
                        const tablesInDataset = data.tables.filter(t => t.dataset === group.dataset)
                        const selectedCount = tables.filter((t) => tablesInDataset.some(dt => dt.table === t)).length
                        const allSelected = tables.every((t) => tablesInDataset.some(dt => dt.table === t)) && tablesInDataset.length > 0
                        const collapsed = needle ? false : !!collapsedDatasets[group.dataset]

                        return (
                          <div key={group.dataset} className={`st-ds-group${collapsed ? ' ds-collapsed' : ''}`}>
                            <div
                              className="st-ds-header"
                              style={selectedCount > 0 ? {
                                background: '#8b5cf614',
                                borderLeft: '3px solid var(--accent)'
                              } : {}}
                              onClick={(e) => {
                                if ((e.target as HTMLElement).closest('button')) return
                                setCollapsedDatasets((c) => ({ ...c, [group.dataset]: !collapsed }))
                              }}
                            >
                              <button
                                type="button"
                                className="ds-toggle-btn"
                                onClick={() => setCollapsedDatasets((c) => ({ ...c, [group.dataset]: !collapsed }))}
                              >
                                {collapsed ? '▶' : '▼'}
                              </button>
                              <span className="ds-name">{group.dataset}</span>
                              <span className="count">
                                {group.tables.length} tables
                                {selectedCount ? (
                                  <>
                                    {' '}
                                    · <b style={{ color: 'var(--accent)' }}>{selectedCount} selected</b>
                                  </>
                                ) : null}
                              </span>
                              <button
                                type="button"
                                className="select-all"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  selectAllInDataset(group.dataset)
                                }}
                              >
                                {allSelected ? 'Clear all' : 'Select all'}
                              </button>
                            </div>
                            {!collapsed && (
                              <div className="ds-table-list">
                                {tables.map((t) => {
                                  const checked = tablesInDataset.some(dt => dt.table === t)
                                  const badge = checked ? colBadge(t) : null
                                  const expanded = checked && expandedTables[t]
                                  // Saved but no longer in what the service account can read.
                                  const noAccess =
                                    checked &&
                                    !loadingCatalog &&
                                    catalog.length > 0 &&
                                    !catalog.some((g) => g.dataset === group.dataset && g.tables.includes(t))
                                  return (
                                    <div key={t} className="st-table-row-wrap">
                                      <label className="st-table-row">
                                        <input
                                          type="checkbox"
                                          checked={checked}
                                          onChange={(e) => toggleTable(group.dataset, t, e.target.checked)}
                                        />
                                        <span>{t}</span>
                                        {noAccess && (
                                          <span
                                            className="st-col-badge no-access"
                                            title="The agent's service account can no longer read this table. Re-grant access in BigQuery or remove it."
                                          >
                                            <AlertTriangle size={10} aria-hidden /> No access
                                          </span>
                                        )}
                                        {badge && (
                                          <span className={`st-col-badge ${badge.cls}`} title={badge.title}>
                                            {badge.text}
                                          </span>
                                        )}
                                        {checked && (
                                          <button
                                            type="button"
                                            className={`st-col-expand-btn${expanded ? ' open' : ''}`}
                                            onClick={(e) => {
                                              e.preventDefault()
                                              e.stopPropagation()
                                              if (expanded) {
                                                setExpandedTables((x) => ({ ...x, [t]: false }))
                                              } else {
                                                openColumnPanel(t, group.dataset)
                                              }
                                            }}
                                          >
                                            {expanded ? '▲' : '▼'} columns
                                          </button>
                                        )}
                                      </label>
                                      {checked && expanded && (
                                        <div className="st-col-panel">
                                          <ColumnPanel
                                            table={t}
                                            schema={schemaCache[t]}
                                            loading={!!schemaLoading[t]}
                                            error={schemaError[t]}
                                            columnFilters={data.column_filters}
                                            valueFilters={data.column_value_filters}
                                            onToggleColumn={toggleColumn}
                                            onSelectAll={selectAllColumns}
                                            onSelectNone={selectNoneColumns}
                                            onAddFilter={addRowFilter}
                                            onRemoveFilter={removeRowFilter}
                                          />
                                        </div>
                                      )}
                                    </div>
                                  )
                                })}
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )}

                  <div className="st-hint" style={{ marginTop: 9 }}>
                    {data.tables.length ? (
                      <>
                        Selected: {data.tables.length} table(s) from{' '}
                        {Array.from(new Set(data.tables.map(t => t.dataset))).join(', ')}
                      </>
                    ) : (
                      'No tables selected yet.'
                    )}
                  </div>

                  {(() => {
                    // Same collapsible tab as the Slack digest layout; opens itself when table names need updating.
                    const renamesPending = !!tableRenames && (tableRenames.places > 0 || tableRenames.unpaired.length > 0)
                    const open = premadeOpen || renamesPending
                    const total = data.premade_queries.length
                    const verified = data.premade_queries.filter((q) => q.verified).length
                    const stray = data.premade_queries.filter((q) => strayTables(q.sql).length > 0).length
                    return (
                  <div className={`sl-editor tone-bq${open ? ' open' : ''}`} style={{ marginTop: 32 }}>
                    <button
                      type="button"
                      className="sl-toggle"
                      aria-expanded={open}
                      onClick={() => setPremadeOpen(!open)}
                    >
                      <span className="sl-toggle-icon" aria-hidden>
                        <ListChecks size={16} />
                      </span>
                      <span className="sl-toggle-text">
                        <span className="sl-title">
                          Premade queries <span className="st-yaml-key">premade_queries</span>
                        </span>
                        <span className="sl-toggle-sub">
                          {total
                            ? `${total} quer${total === 1 ? 'y' : 'ies'} · ${verified} verified`
                            : 'No premade queries yet'}
                          {stray > 0 && ` · ${stray} use${stray === 1 ? 's' : ''} tables not selected`}
                          {renamesPending && ' · table names need updating'}
                        </span>
                      </span>
                      <ChevronRight size={18} className="sl-toggle-chevron" aria-hidden />
                    </button>
                    {open && (
                  <div className="sl-body">
                    <div className="st-hint" style={{ marginBottom: 10 }}>
                      Admin-verified SQL paired with natural-language trigger phrases. When a user&apos;s question matches a
                      trigger, the agent uses this SQL directly.
                    </div>
                    {tableRenames && (tableRenames.places > 0 || tableRenames.unpaired.length > 0) && (
                      <div className="st-premade-result" style={{ marginBottom: 12 }}>
                        <div style={{ fontWeight: 600, marginBottom: 6 }}>
                          Tables changed since duplicating. Update table names?
                        </div>
                        {tableRenames.pairs.map(([from, to]) => (
                          <div key={from} className="st-hint">
                            <code>{from}</code> → <code>{to}</code>
                          </div>
                        ))}
                        {tableRenames.unpaired.map((t) => (
                          <div key={t} className="st-error" style={{ display: 'block' }}>
                            Couldn&apos;t pair <code>{t}</code> with one new table. Edit it by hand.
                          </div>
                        ))}
                        {tableRenames.places > 0 && (
                          <>
                            <div className="st-hint" style={{ margin: '6px 0' }}>
                              Updates {tableRenames.places} place{tableRenames.places === 1 ? '' : 's'} in premade
                              queries, context, description and digest SQL.
                            </div>
                            <button type="button" className="st-btn small" onClick={applyTableRenames}>
                              Update table names
                            </button>
                          </>
                        )}
                      </div>
                    )}
                    {!data.premade_queries.length && (
                      <div className="st-hint" style={{ marginBottom: 8, color: 'var(--text-muted)' }}>
                        No premade queries yet. Add one below.
                      </div>
                    )}
                    {data.premade_queries.map((pq, idx) => (
                      <div key={idx} className={`st-premade-card${pq._expanded ? ' expanded' : ''}`}>
                        <div
                          className="st-premade-header"
                          onClick={(e) => {
                            if ((e.target as HTMLElement).closest('button')) return
                            setData((d) => {
                              const premade_queries = [...d.premade_queries]
                              premade_queries[idx] = { ...pq, _expanded: !pq._expanded }
                              return { ...d, premade_queries }
                            })
                          }}
                        >
                          <span className="st-premade-name">{pq.name || 'Unnamed query'}</span>
                          <span className={`st-badge ${pq.verified ? 'verified' : 'draft'}`}>
                            {pq.verified ? '✓ Verified' : 'Draft'}
                          </span>
                          {strayTables(pq.sql).length > 0 && (
                            <span
                              className="st-col-badge no-access"
                              title={`Uses ${strayTables(pq.sql).join(', ')}, not in the selected tables`}
                            >
                              <AlertTriangle size={10} aria-hidden /> Other tables
                            </span>
                          )}
                          <span className="st-premade-qcount">
                            {pq.questions.length} trigger{pq.questions.length !== 1 ? 's' : ''}
                          </span>
                          <button
                            type="button"
                            className="st-premade-toggle"
                            onClick={() =>
                              setData((d) => {
                                const premade_queries = [...d.premade_queries]
                                premade_queries[idx] = { ...pq, _expanded: !pq._expanded }
                                return { ...d, premade_queries }
                              })
                            }
                          >
                            {pq._expanded ? '▲' : '▼'}
                          </button>
                          <button
                            type="button"
                            className="st-premade-delete"
                            title="Delete query"
                            onClick={() =>
                              setData((d) => ({
                                ...d,
                                premade_queries: d.premade_queries.filter((_, i) => i !== idx),
                              }))
                            }
                          >
                            ×
                          </button>
                        </div>
                        {pq._expanded && (
                          <div className="st-premade-body">
                            <div className="st-premade-row">
                              <label>Query name <span className="req">*</span></label>
                              <input
                                type="text"
                                maxLength={120}
                                value={pq.name}
                                placeholder="e.g. top_epc_providers_30d"
                                onChange={(e) => {
                                  const premade_queries = [...data.premade_queries]
                                  premade_queries[idx] = { ...pq, name: e.target.value }
                                  setData((d) => ({ ...d, premade_queries }))
                                }}
                              />
                            </div>
                            <div className="st-premade-row">
                              <label>Description</label>
                              <input
                                type="text"
                                value={pq.description}
                                placeholder="e.g. Top 5 providers by EPC in the last 30 days"
                                onChange={(e) => {
                                  const premade_queries = [...data.premade_queries]
                                  premade_queries[idx] = { ...pq, description: e.target.value }
                                  setData((d) => ({ ...d, premade_queries }))
                                }}
                              />
                            </div>
                            <div className="st-premade-row">
                              <label>
                                Trigger phrases — questions this SQL answers
                                <span className="st-hint" style={{ display: 'inline', marginLeft: 6 }}>
                                  Press Enter to add
                                </span>
                              </label>
                              <ChipInput
                                values={pq.questions}
                                onChange={(questions) => {
                                  const premade_queries = [...data.premade_queries]
                                  premade_queries[idx] = { ...pq, questions }
                                  setData((d) => ({ ...d, premade_queries }))
                                }}
                                placeholder="Type a trigger phrase, press Enter…"
                              />
                            </div>
                            <div className="st-premade-row">
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                                <label style={{ margin: 0 }}>
                                  SQL <span className="req">*</span>
                                  <span className="st-hint" style={{ display: 'inline', marginLeft: 6 }}>
                                    Use <code>{'{project}'}</code> and <code>{'{dataset}'}</code> as placeholders
                                  </span>
                                </label>
                                <button
                                  type="button"
                                  className="st-col-action"
                                  onClick={() => navigator.clipboard.writeText(pq.sql || '')}
                                >
                                  ⎘ Copy
                                </button>
                              </div>
                              {strayTables(pq.sql).length > 0 && (
                                <div className="st-error" style={{ display: 'block', marginBottom: 6 }}>
                                  This query uses {strayTables(pq.sql).join(', ')}, which{' '}
                                  {strayTables(pq.sql).length === 1 ? 'is' : 'are'} not in the selected tables. It will
                                  fail when the agent uses it.
                                </div>
                              )}
                              <div className="pq-sql-wrap">
                                <textarea
                                  className="pq-sql"
                                  spellCheck={false}
                                  value={pq.sql}
                                  placeholder={`SELECT\n  column1\nFROM \`{project}.{dataset}.table\`\nWHERE event_date >= DATE_SUB(CURRENT_DATE(), INTERVAL 30 DAY)\nLIMIT 100`}
                                  onChange={(e) => {
                                    const premade_queries = [...data.premade_queries]
                                    premade_queries[idx] = { ...pq, sql: e.target.value }
                                    setData((d) => ({ ...d, premade_queries }))
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Tab') {
                                      e.preventDefault()
                                      const ta = e.currentTarget
                                      const start = ta.selectionStart
                                      const end = ta.selectionEnd
                                      const next = pq.sql.slice(0, start) + '  ' + pq.sql.slice(end)
                                      const premade_queries = [...data.premade_queries]
                                      premade_queries[idx] = { ...pq, sql: next }
                                      setData((d) => ({ ...d, premade_queries }))
                                      requestAnimationFrame(() => {
                                        ta.selectionStart = ta.selectionEnd = start + 2
                                      })
                                    }
                                  }}
                                />
                              </div>
                            </div>
                            <div className="st-premade-actions">
                              <label className="st-toggle" style={{ marginRight: 12 }}>
                                <input
                                  type="checkbox"
                                  checked={pq.verified}
                                  onChange={(e) => {
                                    const premade_queries = [...data.premade_queries]
                                    premade_queries[idx] = { ...pq, verified: e.target.checked }
                                    setData((d) => ({ ...d, premade_queries }))
                                  }}
                                />
                                <span className="track" />
                                <span className="lbl">Verified</span>
                              </label>
                              <button
                                type="button"
                                className="st-btn small"
                                disabled={!pq.sql.trim() || !!pq._testing}
                                onClick={async () => {
                                  const premade_queries = [...data.premade_queries]
                                  premade_queries[idx] = { ...pq, _testing: true, _testResult: null }
                                  setData((d) => ({ ...d, premade_queries }))
                                  try {
                                    const res = await api.post(
                                      `/api/agents/${encodeURIComponent(data.task_name || '_test')}/queries/test`,
                                      {
                                        sql: pq.sql,
                                        bq_project: data.bq_project,
                                        bq_dataset: data.bq_dataset,
                                        tables: data.tables.map((t) => `${t.dataset}.${t.table}`),
                                      }
                                    )
                                    setData((d) => {
                                      const list = [...d.premade_queries]
                                      list[idx] = { ...list[idx], _testing: false, _testResult: res.data }
                                      return { ...d, premade_queries: list }
                                    })
                                  } catch (err: any) {
                                    setData((d) => {
                                      const list = [...d.premade_queries]
                                      list[idx] = {
                                        ...list[idx],
                                        _testing: false,
                                        _testResult: {
                                          error: extractErrorMessage(err, 'Query failed'),
                                        },
                                      }
                                      return { ...d, premade_queries: list }
                                    })
                                  }
                                }}
                              >
                                {pq._testing ? 'Testing…' : '▶ Test query'}
                              </button>
                            </div>
                            {pq._testResult && (
                              <div className="st-premade-result">
                                {pq._testResult.error ? (
                                  <div className="st-error" style={{ display: 'block' }}>
                                    Query failed: {pq._testResult.error}
                                  </div>
                                ) : (
                                  <>
                                    <div className="st-hint">
                                      {pq._testResult.row_count ?? 0} row
                                      {(pq._testResult.row_count ?? 0) !== 1 ? 's' : ''} ·{' '}
                                      {(((pq._testResult.bytes_processed || 0) as number) / 1e6).toFixed(1)} MB scanned
                                    </div>
                                    <ResultTable
                                      columns={pq._testResult.columns || []}
                                      rows={pq._testResult.rows || []}
                                    />
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      className="st-btn small"
                      onClick={() =>
                        setData((d) => ({
                          ...d,
                          premade_queries: [
                            ...d.premade_queries,
                            {
                              name: '',
                              description: '',
                              sql: '',
                              questions: [],
                              verified: false,
                              _expanded: true,
                              _testing: false,
                              _testResult: null,
                            },
                          ],
                        }))
                      }
                    >
                      + Add query
                    </button>
                  </div>
                    )}
                  </div>
                    )
                  })()}
                </>
                )}
                    </div>
                  )}

                  {/* Charts Configuration */}
                  {data.skills.includes('charts') && (
                    <div className="st-skill-config-section">
                      <h3 
                        className="st-skill-config-title"
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => setChartsSkillExpanded(!chartsSkillExpanded)}
                      >
                        <span style={{ marginRight: 8 }}>{chartsSkillExpanded ? '▼' : '▶'}</span>
                        <BarChart3 size={24} strokeWidth={2} />
                        Chart Visualization
                      </h3>
                      {chartsSkillExpanded && (
                      <div className="st-hint">
                        Chart generation will be automatically available in the chat UI. No additional configuration needed.
                      </div>
                      )}
                    </div>
                  )}
                  
                  {/* Slack Configuration */}
                  {data.skills.includes('slack') && (
                    <div className="st-skill-config-section">
                      <h3 
                        className="st-skill-config-title" 
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => setSlackSkillExpanded(!slackSkillExpanded)}
                      >
                        <span style={{ marginRight: 8 }}>{slackSkillExpanded ? '▼' : '▶'}</span>
                        <Slack size={24} strokeWidth={2} />
                        Slack & Digests
                      </h3>
                {slackSkillExpanded && (
                <>
                  <div className="st-field">
                    <label className="st-toggle">
                      <input
                        type="checkbox"
                        checked={data.digest_enabled}
                        onChange={(e) => setData((d) => ({ ...d, digest_enabled: e.target.checked }))}
                      />
                      <span className="track" />
                      <span className="lbl">Daily Slack digest</span>
                    </label>
                    <div className="st-hint">Posts a daily performance briefing to Slack on the schedule you set.</div>
                  </div>

                  {data.digest_enabled && (
                    <>
                    <div className="st-subpanel">
                      <div className="st-field">
                        <label className="st-toggle">
                          <input
                            type="checkbox"
                            checked={!!data.digest_snapshot_sql.trim()}
                            onChange={(e) => {
                              if (e.target.checked) {
                                // Enabling - set a placeholder comment so editor appears
                                setData((d) => ({ ...d, digest_snapshot_sql: '-- Paste or write your digest SQL here\n' }))
                              } else {
                                // Disabling - clear the SQL
                                setData((d) => ({ ...d, digest_snapshot_sql: '' }))
                              }
                            }}
                          />
                          <span className="track" />
                          <span className="lbl">Build from a template (recommended)</span>
                        </label>
                        <div className="st-hint">
                          {data.digest_snapshot_sql.trim()
                            ? 'Tables come from a tested query, so the numbers are exact.'
                            : 'The AI answers the digest questions below instead.'}
                        </div>
                      </div>

                      {data.digest_snapshot_sql.trim() && (
                        <div className="st-field">
                          <label>Digest template</label>
                          <div className="st-premade-actions" style={{ marginTop: 0 }}>
                            <select
                              aria-label="Digest SQL template"
                              value={selectedTemplate}
                              onChange={(e) => setSelectedTemplate(e.target.value)}
                              className="st-template-select"
                            >
                              <option value="">Choose a template…</option>
                              {sqlTemplates.map((tpl) => {
                                const missing = missingTables(tpl)
                                return (
                                  <option key={tpl.id} value={tpl.id} disabled={missing.length > 0}>
                                    {missing.length ? `${tpl.name} — needs a ${missing.join('… and a ')}… table` : tpl.name}
                                  </option>
                                )
                              })}
                            </select>
                            <button
                              type="button"
                              className="st-btn small sl-inline"
                              disabled={!selectedTemplate || !!generatingTemplate || !data.bq_project}
                              onClick={async () => {
                                const tpl = sqlTemplates.find((t) => t.id === selectedTemplate)
                                if (!tpl) return
                                setGeneratingTemplate(tpl.id)
                                setDigestSqlTestResult(null)
                                try {
                                  const res = await api.post(
                                    `/api/agents/${encodeURIComponent(data.task_name || '_test')}/digest-sql/generate`,
                                    {
                                      bq_project: data.bq_project,
                                      tables: data.tables.map((t) => `${t.dataset}.${t.table}`),
                                      template_id: tpl.id,
                                    }
                                  )
                                  setData((d) => ({ ...d, digest_snapshot_sql: res.data.template, digest_template: tpl.id, digest_layout: [] }))
                                  setMetricCatalog(null)
                                  setCatalogStatus({ state: 'idle' })
                                  setDigestSqlTestResult({ success: `Generated from ${tpl.name}` })
                                } catch (err: any) {
                                  setDigestSqlTestResult({ error: extractErrorMessage(err, 'Failed to generate SQL') })
                                } finally {
                                  setGeneratingTemplate('')
                                }
                              }}
                            >
                              {generatingTemplate ? <Loader2 size={13} className="st-spin" aria-hidden /> : <Wand2 size={13} aria-hidden />}
                              Generate
                            </button>
                            <button
                              type="button"
                              className="st-btn small sl-inline"
                              aria-expanded={sqlOpen}
                              onClick={() => setSqlOpen(!sqlOpen)}
                            >
                              <Code2 size={13} aria-hidden />
                              {sqlOpen ? 'Hide SQL' : 'Show SQL'}
                            </button>
                            <button
                              type="button"
                              className="sl-preview-btn sl-test-btn"
                              disabled={!data.bq_project || !data.bq_dataset || !data.digest_snapshot_sql.trim() || digestSqlTesting}
                              onClick={testSnapshotSql}
                              title={digestSqlTesting ? 'Running the snapshot SQL…' : 'Run the snapshot SQL and show its rows'}
                            >
                              {digestSqlTesting ? <Loader2 size={14} className="st-spin" aria-hidden /> : <Play size={14} aria-hidden />}
                              Test query
                            </button>
                          </div>
                          {sqlTemplates.length > 0 && sqlTemplates.every((tpl) => missingTables(tpl).length) ? (
                            <div className="st-error" style={{ display: 'block', marginTop: 8 }}>
                              These tables don&apos;t match any template. A new template is needed for this dataset.
                            </div>
                          ) : (
                            <div className="st-hint">
                              {sqlTemplates.find((t) => t.id === selectedTemplate)?.description ||
                                'Pick a template, then Generate to build the digest from the tables selected above.'}
                            </div>
                          )}
                          {sqlOpen && (
                            <>
                              <div className="pq-sql-wrap">
                                <textarea
                                  className="pq-sql"
                                  spellCheck={false}
                                  value={data.digest_snapshot_sql}
                                  onChange={(e) => setData((d) => ({ ...d, digest_snapshot_sql: e.target.value }))}
                                  placeholder={`-- ONE-SHOT DIGEST SNAPSHOT SQL
WITH params AS (
  SELECT DATE '{analysis_date}' AS ad
),
...`}
                                  rows={15}
                                />
                              </div>
                              <div className="st-hint" style={{ marginTop: 8 }}>
                                Use <code>{'{analysis_date}'}</code> placeholder. The engine substitutes it at runtime.
                              </div>
                            </>
                          )}
                          {layoutRows.length > 0 && performanceRow && (
                            <SlackLayoutEditor
                              performance={performanceRow}
                              onPerformanceChange={setPerformance}
                              previewing={previewing}
                              canPreview={!!data.bq_project && !!data.digest_snapshot_sql.trim()}
                              onPreview={previewSlack}
                              rows={layoutRows}
                              metricOptions={metricOptions}
                              catalog={metricCatalog}
                              status={shownCatalogStatus}
                              source={layoutTemplate?.name || ''}
                              onChange={setLayout}
                              onRefresh={loadMetricCatalog}
                            />
                          )}
                          {digestSqlTestResult && (
                            <div className="st-premade-result">
                              {digestSqlTestResult.error ? (
                                <div className="st-error" style={{ display: 'block' }}>
                                  {digestSqlTestResult.error}
                                </div>
                              ) : digestSqlTestResult.success ? (
                                <div className="st-hint" style={{ color: '#10b981' }}>
                                  {digestSqlTestResult.success}
                                </div>
                              ) : null}
                            </div>
                          )}
                        </div>
                      )}

                      {!data.digest_snapshot_sql.trim() && (
                        <div className="st-field">
                          <label>Daily digest questions <span className="req">*</span></label>
                          {data.questions.map((q, i) => (
                            <div key={i} className="st-q-row">
                              <input
                                type="text"
                                value={q}
                                placeholder="e.g. What drove revenue this week?"
                                onChange={(e) => {
                                  const questions = [...data.questions]
                                  questions[i] = e.target.value
                                  setData((d) => ({ ...d, questions }))
                                }}
                              />
                              <button
                                type="button"
                                className="st-q-remove"
                                onClick={() => {
                                  const questions = data.questions.filter((_, idx) => idx !== i)
                                  setData((d) => ({ ...d, questions: questions.length ? questions : [''] }))
                                }}
                              >
                                ×
                              </button>
                            </div>
                          ))}
                          <button
                            type="button"
                            className="st-btn small"
                            onClick={() => setData((d) => ({ ...d, questions: [...d.questions, ''] }))}
                          >
                            + Add question
                          </button>
                          <div className="st-hint">
                            These questions will be asked daily and results sent to Slack.
                          </div>
                        </div>
                      )}

                    </div>

                      <div className="st-field">
                        <label>Run every day at</label>
                        <div style={{ display: 'flex', gap: 9, alignItems: 'center' }}>
                          <select
                            style={{ width: 150 }}
                            disabled={data.custom_cron}
                            value={data.schedule_hour}
                            onChange={(e) => {
                              const hour = +e.target.value
                              setData((d) => ({
                                ...d,
                                schedule_hour: hour,
                                schedule: `0 ${hour} * * *`,
                              }))
                            }}
                          >
                            {Array.from({ length: 24 }, (_, h) => (
                              <option key={h} value={h}>
                                {String(h).padStart(2, '0')}:00 UTC
                              </option>
                            ))}
                          </select>
                          <label className="st-toggle" style={{ marginLeft: 6 }}>
                            <input
                              type="checkbox"
                              checked={data.custom_cron}
                              onChange={(e) => {
                                const on = e.target.checked
                                setData((d) => ({
                                  ...d,
                                  custom_cron: on,
                                  schedule: on ? d.schedule : `0 ${d.schedule_hour} * * *`,
                                }))
                              }}
                            />
                            <span className="track" />
                            <span className="lbl">Custom cron</span>
                          </label>
                        </div>
                      </div>

                      {data.custom_cron && (
                        <div className="st-field">
                          <label>Cron expression (UTC)</label>
                          <input
                            type="text"
                            value={data.schedule}
                            placeholder="0 9 * * *"
                            onChange={(e) => setData((d) => ({ ...d, schedule: e.target.value }))}
                          />
                        </div>
                      )}

                      <div className="st-hint" style={{ marginBottom: 14 }}>
                        {cronPreview}
                      </div>

                      <div className="st-field">
                        <label>Slack channel</label>
                        <select
                          value={data.slack_channel_id || data.slack_channel}
                          onChange={(e) => {
                            const ch = channels.find((c) => c.id === e.target.value || c.name === e.target.value)
                            setData((d) => ({
                              ...d,
                              slack_channel: ch?.name || '',
                              slack_channel_id: ch?.id || e.target.value,
                            }))
                          }}
                        >
                          <option value="">— default channel —</option>
                          {channels.map((c) => (
                            <option key={c.id} value={c.id}>
                              #{c.name}
                              {c.label && c.label !== c.name ? ` — ${c.label}` : ''}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="st-field">
                        <label>
                          Vertical tags <span className="st-yaml-key">slack_vertical</span>
                        </label>
                        <ChipInput
                          values={data.verticals}
                          onChange={(verticals) => setData((d) => ({ ...d, verticals }))}
                          placeholder="e.g. DEMAND_PULSE — press Enter"
                        />
                      </div>


                    </>
                  )}
                </>
                )}
                    </div>
                  )}

                  {/* Demand Pulse: pick the search-demand datasets this agent can use */}
                  {data.skills.includes('demand_pulse') && (
                    <div className="st-skill-config-section">
                      <h3
                        className="st-skill-config-title"
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => setDemandPulseSkillExpanded(!demandPulseSkillExpanded)}
                      >
                        <span style={{ marginRight: 8 }}>{demandPulseSkillExpanded ? '▼' : '▶'}</span>
                        <Activity size={22} style={{ marginRight: 8 }} />
                        Demand Pulse
                      </h3>
                      {demandPulseSkillExpanded && (
                      <div className="st-field">
                        <label>
                          Demand Pulse datasets <span className="req">*</span>
                        </label>
                        <div className="st-hint" style={{ marginBottom: 8 }}>
                          Google search demand (keywords, categories, mindsets{dpDatasets.some((d) => d.has_forecast) ? ', forecasts' : ''}).
                        </div>
                        {dpLoading && <div className="st-hint">Loading datasets…</div>}
                        {dpError && <div className="st-error">{dpError}</div>}
                        {!dpLoading &&
                          !dpError &&
                          dpDatasets.map((d) => (
                            <label key={d.id} className="st-col-row">
                              <input
                                type="checkbox"
                                className="st-col-cb"
                                checked={data.demand_pulse_datasets.includes(d.id)}
                                onChange={(e) => toggleDemandPulseDataset(d.id, e.target.checked)}
                              />
                              <span className="st-col-name">{d.label}</span>
                              <span className="st-hint" style={{ marginLeft: 8 }}>
                                {d.has_forecast ? 'with forecast' : 'search volume'}
                              </span>
                              {d.recommended && (
                                <span className="st-badge verified" style={{ marginLeft: 8 }}>
                                  Recommended
                                </span>
                              )}
                            </label>
                          ))}
                        {!dpLoading && !dpError && dpDatasets.length > 0 && !dpDatasets.some((d) => d.recommended) && (
                          <div className="st-hint">No dataset matches this agent's name — pick one manually.</div>
                        )}
                      </div>
                      )}
                    </div>
                  )}

                  {/* Google Ads Configuration */}
                  {data.skills.includes('google_ads') && (
                    <div className="st-skill-config-section">
                      <h3 
                        className="st-skill-config-title" 
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => setGoogleAdsSkillExpanded(!googleAdsSkillExpanded)}
                      >
                        <span style={{ marginRight: 8 }}>{googleAdsSkillExpanded ? '▼' : '▶'}</span>
                        <GoogleAdsIcon size={24} />
                        Google Ads Management
                        <BetaBadge />
                      </h3>
                      {googleAdsSkillExpanded && (
                        <>
                          {/* Managed Accounts — campaign picker */}
                          <GoogleAdsCampaignPicker
                            campaigns={gadsCampaigns}
                            loading={gadsCampaignsLoading && !gadsLoadedOnce}
                            refreshing={gadsCampaignsLoading && gadsLoadedOnce}
                            fetchError={gadsCampaignsError}
                            selectedCustomerIds={data.google_ads_customer_ids || []}
                            selectedCampaignIds={gadsSelectedCampaignIds}
                            setSelectedCampaignIds={setGadsSelectedCampaignIds}
                            onSelectionChange={(customerIds, campaignIds) =>
                              setData((d) => ({
                                ...d,
                                google_ads_customer_ids: customerIds,
                                google_ads_campaign_ids: campaignIds,
                              }))
                            }
                            access={gadsAccess.access}
                            checkingAccess={gadsAccess.checking}
                            onRecheckAccess={gadsAccess.recheck}
                            onRefresh={refreshGoogleAds}
                          />

                          {/* Business Targets */}
                          <div className="st-field">
                            <label>What are your campaign goals? <span className="req">*</span></label>
                            <div className="st-hint" style={{ marginBottom: 8 }}>
                              Describe your goals in plain English. The AI will interpret these and optimize accordingly.
                            </div>
                            <textarea
                              rows={3}
                              value={(data.google_ads_config && data.google_ads_config.targets) || ''}
                              onChange={(e) => setData((d) => ({
                                ...d,
                                google_ads_config: { 
                                  ...DEFAULT_GOOGLE_ADS_CONFIG,
                                  ...(d.google_ads_config || {}), 
                                  targets: e.target.value 
                                }
                              }))}
                              placeholder="Example: Keep CPA under $50, maximize conversions, prioritize brand keywords, scale high ROAS campaigns"
                            />
                          </div>

                          {/* ML Model Selection: collapsed by default, summary shows the current choice */}
                          <div className="st-field gads-collapsible">
                            <button
                              type="button"
                              className="gads-collapsible-head"
                              aria-expanded={gadsModelOpen}
                              aria-controls="gads-model-settings"
                              onClick={() => setGadsModelOpen((o) => !o)}
                            >
                              <span className="gads-collapsible-title">AI optimization model</span>
                              <span className="gads-collapsible-summary">
                                {GADS_MODEL_LABEL[(data.google_ads_config && data.google_ads_config.ml_model) || 'logistic_regression'] ?? 'Statistics only'}
                                {((data.google_ads_config && data.google_ads_config.ml_model) || 'logistic_regression') !== 'none' &&
                                  ` · ${(data.google_ads_config && data.google_ads_config.ml_training_days) || 90} days of history`}
                              </span>
                              <ChevronDown size={16} aria-hidden className={`gads-chevron${gadsModelOpen ? ' open' : ''}`} />
                            </button>
                          </div>
                          {gadsModelOpen && (
                            <div id="gads-model-settings" className="gads-collapsible-body">
                            <div className="st-field">
                              <label>AI optimization model</label>
                              <select 
                                value={(data.google_ads_config && data.google_ads_config.ml_model) || 'logistic_regression'}
                                onChange={(e) => setData((d) => ({
                                  ...d,
                                  google_ads_config: { 
                                    ...DEFAULT_GOOGLE_ADS_CONFIG,
                                    ...(d.google_ads_config || {}), 
                                    ml_model: e.target.value 
                                  }
                                }))}
                              >
                                <option value="none">Statistics only (no forecast)</option>
                                <option value="logistic_regression">Poisson GLM (recommended for a single account)</option>
                                <option value="gradient_boosting">Gradient boosting, Poisson objective (LightGBM-style)</option>
                                <option value="random_forest">Random forest, Poisson splits</option>
                              </select>
                            </div>

                            {((data.google_ads_config && data.google_ads_config.ml_model) || 'logistic_regression') !== 'none' && (
                              <div className="st-field" style={{ marginLeft: '1.5rem' }}>
                                <label>Training data window</label>
                                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                                  <input 
                                    type="number" 
                                    min="45"
                                    max="180"
                                    value={(data.google_ads_config && data.google_ads_config.ml_training_days) || 90}
                                    onChange={(e) => setData((d) => ({
                                      ...d,
                                      google_ads_config: { ...DEFAULT_GOOGLE_ADS_CONFIG, ...(d.google_ads_config || {}), ml_training_days: +e.target.value }
                                    }))}
                                  />
                                  <span style={{ color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>days of history</span>
                                </div>
                                <div className="st-hint">At least 45 days are needed to test the forecast. 90 days recommended.</div>
                              </div>
                            )}
                            </div>
                          )}

                      {/* Optimization Schedule */}
                      <div className="st-field">
                        <label>How often should the agent check campaigns?</label>
                        <select 
                          value={(data.google_ads_config && data.google_ads_config.check_frequency) || 'daily'}
                          onChange={(e) => setData((d) => ({
                            ...d,
                            google_ads_config: { ...DEFAULT_GOOGLE_ADS_CONFIG, ...(d.google_ads_config || {}), check_frequency: e.target.value }
                          }))}
                        >
                          <option value="hourly">Every hour</option>
                          <option value="every_6_hours">Every 6 hours</option>
                          <option value="daily">Daily (recommended)</option>
                          <option value="weekly">Weekly</option>
                        </select>
                        <div className="st-hint">
                          The agent will analyze performance, train AI models, and apply optimizations on this schedule.
                        </div>
                      </div>

                          {/* Mode: Safe vs Live, narrowed per account by the access each advertiser granted */}
                          <ModeCard
                            safe={gadsSafe}
                            onChange={setGadsSafe}
                            summary={gadsModeSummary}
                            budgetStepPct={Math.min(
                              Number(data.google_ads_config?.auto_approve_budget_change_percent ?? DEFAULT_GOOGLE_ADS_CONFIG.auto_approve_budget_change_percent) || 15,
                              20,
                            )}
                            portfolioCapPct={Number(data.google_ads_config?.max_total_budget_increase_pct ?? DEFAULT_GOOGLE_ADS_CONFIG.max_total_budget_increase_pct) || 10}
                          />

                          {/* Auto-Actions */}
                          <h4 style={{ marginTop: 32, marginBottom: 8 }}>What can the agent do automatically?</h4>
                          <div className="st-hint" style={{ marginBottom: 16 }}>
                            The agent will perform these actions when optimization conditions are met.
                            {(gadsSafe || gadsModeSummary.allReportOnly) && (
                              <strong style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, color: 'var(--accent)' }}>
                                <AlertTriangle size={14} />{' '}
                                {gadsSafe
                                  ? 'Safe mode is on: these show up as recommendations and are not applied.'
                                  : 'All selected accounts are report only: these show up as recommendations and are not applied.'}
                              </strong>
                            )}
                          </div>

                          <div className="st-field">
                            <label className="st-toggle">
                              <input 
                                type="checkbox" 
                                checked={(data.google_ads_config && data.google_ads_config.auto_pause_campaigns) !== false}
                                onChange={(e) => setData((d) => ({
                                  ...d,
                                  google_ads_config: { ...DEFAULT_GOOGLE_ADS_CONFIG, ...(d.google_ads_config || {}), auto_pause_campaigns: e.target.checked }
                                }))}
                              />
                              <span className="track" />
                              <span className="lbl">Pause underperforming campaigns</span>
                            </label>
                          </div>

                          <div className="st-field">
                            <label className="st-toggle">
                              <input 
                                type="checkbox" 
                                checked={(data.google_ads_config && data.google_ads_config.auto_adjust_budgets) !== false}
                                onChange={(e) => setData((d) => ({
                                  ...d,
                                  google_ads_config: { ...DEFAULT_GOOGLE_ADS_CONFIG, ...(d.google_ads_config || {}), auto_adjust_budgets: e.target.checked }
                                }))}
                              />
                              <span className="track" />
                              <span className="lbl">Adjust campaign budgets (increase winners, decrease losers)</span>
                            </label>
                          </div>

                          {((data.google_ads_config && data.google_ads_config.auto_adjust_budgets) !== false) && (
                            <div className="st-field" style={{ marginLeft: '1.5rem' }}>
                              <label>Auto-approve budget changes up to</label>
                              <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 8 }}>
                                <input 
                                  type="range" 
                                  min="0" 
                                  max="50" 
                                  step="1"
                                  style={{ flex: 1, minWidth: 200 }}
                                  value={(data.google_ads_config && data.google_ads_config.auto_approve_budget_change_percent) || 15}
                                  onChange={(e) => setData((d) => ({
                                    ...d,
                                    google_ads_config: { ...DEFAULT_GOOGLE_ADS_CONFIG, ...(d.google_ads_config || {}), auto_approve_budget_change_percent: +e.target.value }
                                  }))}
                                />
                                <input 
                                  type="number" 
                                  min="0" 
                                  max="50" 
                                  step="1"
                                  style={{ width: 70, textAlign: 'center', fontWeight: 600 }}
                                  value={(data.google_ads_config && data.google_ads_config.auto_approve_budget_change_percent) || 15}
                                  onChange={(e) => setData((d) => ({
                                    ...d,
                                    google_ads_config: { ...DEFAULT_GOOGLE_ADS_CONFIG, ...(d.google_ads_config || {}), auto_approve_budget_change_percent: Math.min(50, Math.max(0, +e.target.value || 0)) }
                                  }))}
                                />
                                <span style={{ color: 'var(--text-muted)' }}>%</span>
                              </div>
                              <div className="st-hint">
                                Budget changes above {(data.google_ads_config && data.google_ads_config.auto_approve_budget_change_percent) || 15}% require manual review before applying.
                              </div>
                            </div>
                          )}

                          <div className="st-field">
                            <label className="st-toggle">
                              <input 
                                type="checkbox" 
                                checked={(data.google_ads_config && data.google_ads_config.auto_add_keywords) !== false}
                                onChange={(e) => setData((d) => ({
                                  ...d,
                                  google_ads_config: { ...DEFAULT_GOOGLE_ADS_CONFIG, ...(d.google_ads_config || {}), auto_add_keywords: e.target.checked }
                                }))}
                              />
                              <span className="track" />
                              <span className="lbl">Add new keywords from high-converting search terms</span>
                            </label>
                          </div>

                          <div className="st-field">
                            <label className="st-toggle">
                              <input 
                                type="checkbox" 
                                checked={(data.google_ads_config && data.google_ads_config.auto_add_negatives) !== false}
                                onChange={(e) => setData((d) => ({
                                  ...d,
                                  google_ads_config: { ...DEFAULT_GOOGLE_ADS_CONFIG, ...(d.google_ads_config || {}), auto_add_negatives: e.target.checked }
                                }))}
                              />
                              <span className="track" />
                              <span className="lbl">Block wasteful keywords (add as negatives)</span>
                            </label>
                          </div>

                          <div className="st-field">
                            <label className="st-toggle">
                              <input
                                type="checkbox"
                                checked={data.google_ads_config?.auto_remove_keywords !== false}
                                onChange={(e) => setData((d) => ({
                                  ...d,
                                  google_ads_config: { ...DEFAULT_GOOGLE_ADS_CONFIG, ...(d.google_ads_config || {}), auto_remove_keywords: e.target.checked }
                                }))}
                              />
                              <span className="track" />
                              <span className="lbl">Pause keywords that spend without converting</span>
                            </label>
                          </div>

                          <div className="st-field">
                            <label>Slack channel for Google Ads reports</label>
                            <select
                              value={data.google_ads_config?.slack_channel_id || ''}
                              onChange={(e) => {
                                const ch = channels.find((c) => c.id === e.target.value)
                                setData((d) => ({
                                  ...d,
                                  google_ads_config: {
                                    ...DEFAULT_GOOGLE_ADS_CONFIG,
                                    ...(d.google_ads_config || {}),
                                    slack_channel_id: ch?.id || '',
                                    slack_channel: ch?.name || '',
                                  },
                                }))
                              }}
                            >
                              <option value="">— default: #pearson-sem-test —</option>
                              {channels.map((c) => (
                                <option key={c.id} value={c.id}>
                                  #{c.name}
                                  {c.label && c.label !== c.name ? ` — ${c.label}` : ''}
                                </option>
                              ))}
                            </select>
                            <div className="st-hint">
                              While Google Ads is in development, every report goes to #pearson-sem-test; this choice takes over afterwards.
                            </div>
                          </div>

                          {isEditMode && data.task_name && (
                            <div className="st-field">
                              <label>Test this agent now</label>
                              <div className="st-hint" style={{ marginBottom: 8 }}>
                                Runs the optimizer with the settings currently in this form, saved or not, and shows the report.{' '}
                                {gadsModeSummary.text}
                              </div>
                              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                <button type="button" className="st-btn" disabled={gadsRunBusy} onClick={() => runGoogleAdsNow(false)}>
                                  {gadsRunBusy
                                    ? 'Running…'
                                    : gadsModeSummary.liveCount > 0
                                      ? `Run now (live in ${gadsModeSummary.liveCount} account${gadsModeSummary.liveCount === 1 ? '' : 's'})`
                                      : 'Run now (safe)'}
                                </button>
                                <button type="button" className="st-btn" disabled={gadsRunBusy} onClick={() => runGoogleAdsNow(true)}>
                                  Run now and post to Slack
                                </button>
                              </div>
                              {gadsRunBusy && (
                                <div className="st-hint" style={{ marginTop: 8 }} aria-live="polite">
                                  Checking {(data.google_ads_customer_ids || []).length || 'the selected'} account
                                  {(data.google_ads_customer_ids || []).length === 1 ? '' : 's'}… usually under a minute, up to a few
                                  minutes for large accounts.
                                </div>
                              )}
                              {gadsRunError && (
                                <div className="st-hint" style={{ color: 'var(--danger, #e74c3c)', marginTop: 8 }}>{gadsRunError}</div>
                              )}
                              {gadsRunResult && (
                                <RunOutcomeTable accounts={gadsRunResult.accounts || []} decisions={gadsRunResult.decisions || []} />
                              )}
                              {gadsRunReport && (
                                <pre style={{ whiteSpace: 'pre-wrap', marginTop: 8, padding: 12, border: '1px solid var(--border)', borderRadius: 6, fontSize: 12, maxHeight: 420, overflowY: 'auto' }}>
                                  {gadsRunReport}
                                </pre>
                              )}
                            </div>
                          )}

                          {/* Advanced Settings */}
                          <details style={{ marginTop: 24 }}>
                            <summary style={{ cursor: 'pointer', fontWeight: 600, marginBottom: 12, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 6 }}>
                              <Settings size={16} /> Advanced settings
                            </summary>
                            <div className="st-field">
                              <label>Keywords per batch operation</label>
                              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                                <input 
                                  type="number" 
                                  min="100" 
                                  max="5000" 
                                  step="100"
                                  value={(data.google_ads_config && data.google_ads_config.batch_size) || 1000}
                                  onChange={(e) => setData((d) => ({
                                    ...d,
                                    google_ads_config: { ...DEFAULT_GOOGLE_ADS_CONFIG, ...(d.google_ads_config || {}), batch_size: +e.target.value }
                                  }))}
                                />
                                <span style={{ color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>keywords</span>
                              </div>
                              <div className="st-hint">
                                Higher values = faster execution but less granular error handling. 1000 recommended.
                              </div>
                            </div>
                            <div className="st-field">
                              <label>Account-wide budget increase cap per run</label>
                              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                                <input
                                  type="number"
                                  min="0"
                                  max="50"
                                  step="1"
                                  value={data.google_ads_config?.max_total_budget_increase_pct ?? DEFAULT_GOOGLE_ADS_CONFIG.max_total_budget_increase_pct}
                                  onChange={(e) => setData((d) => ({
                                    ...d,
                                    google_ads_config: { ...DEFAULT_GOOGLE_ADS_CONFIG, ...(d.google_ads_config || {}), max_total_budget_increase_pct: +e.target.value }
                                  }))}
                                />
                                <span style={{ color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>%</span>
                              </div>
                              <div className="st-hint">Total daily budget across the selected campaigns can grow by at most this much per run. Budget cut from weak campaigns frees room for strong ones.</div>
                            </div>
                            <div className="st-field">
                              <label>Maximum daily budget per campaign</label>
                              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                                <input
                                  type="number"
                                  min="1"
                                  max="1000000"
                                  step="100"
                                  value={data.google_ads_config?.max_daily_budget_usd ?? DEFAULT_GOOGLE_ADS_CONFIG.max_daily_budget_usd}
                                  onChange={(e) => setData((d) => ({
                                    ...d,
                                    google_ads_config: { ...DEFAULT_GOOGLE_ADS_CONFIG, ...(d.google_ads_config || {}), max_daily_budget_usd: +e.target.value }
                                  }))}
                                />
                                <span style={{ color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>USD/day</span>
                              </div>
                              <div className="st-hint">The agent never raises a campaign's daily budget above this.</div>
                            </div>
                          </details>
                        </>
                      )}
                    </div>
                  )}

                </>
              )}

              <div className="st-wizard-footer">
                <button
                  type="button"
                  className="st-btn"
                  onClick={() => {
                    if (step === 1) navigate(-1)
                    else setStep((s) => s - 1)
                  }}
                >
                  {step === 1 ? 'Cancel' : '← Back'}
                </button>
                <button
                  type="button"
                  className="st-btn primary"
                  disabled={busy === 'save'}
                  onClick={() => {
                    if (step === 3) submit()
                    else if (validateStep(step)) setStep((s) => s + 1)
                  }}
                >
                  {step === 3
                    ? busy === 'save'
                      ? 'Saving…'
                      : 'Save Data Agent'
                    : 'Next →'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>

      {showCtxEditor && (
        <div 
          className={`ctx-editor-overlay visible`} 
          style={{ left: sidebarOpen ? '220px' : '0' }}
          onClick={(e) => e.target === e.currentTarget && setShowCtxEditor(false)}
        >          <div className="ctx-editor">
            <div className="ctx-editor-head">
              <div>
                <h3>Dataset context</h3>
                <p>The agent reads this before every question — domain knowledge, key metrics, table roles and join rules.</p>
              </div>
              <button type="button" className="ctx-editor-close" onClick={() => setShowCtxEditor(false)}>
                ✕
              </button>
            </div>
            <div className="ctx-editor-body">
              <textarea
                className="ctx-editor-area"
                spellCheck={false}
                value={data.context}
                onChange={(e) => setData((d) => ({ ...d, context: e.target.value }))}
                placeholder="Describe what this data tracks, key metrics, table roles, and any important join rules."
              />
            </div>
            <div className="ctx-editor-foot">
              <span className="ctx-editor-count">{data.context.length.toLocaleString()} chars</span>
              <button type="button" className="st-btn primary" onClick={() => setShowCtxEditor(false)}>
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
